import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { SpeechRecognition as NativeSpeech } from '@capacitor-community/speech-recognition';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { SocialLogin } from '@capgo/capacitor-social-login';
import './App.css';

// Единая точка обработки сетевых ошибок для ВСЕХ запросов в приложении.
// Если err.response отсутствует - значит запрос вообще не дошёл до сервера
// (нет интернета, обрыв соединения, сервер недоступен), а не ошибка от бэкенда.
// Подставляем понятный текст в то же место (err.response.data.detail), которое
// уже читают все существующие catch-блоки - никого из них трогать не пришлось.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      error.response = { data: { detail: 'Проверьте соединение с интернетом' } };
    }
    return Promise.reject(error);
  }
);

const API_URL = '/api';
const GOOGLE_WEB_CLIENT_ID = '832471627623-cdanihsci7m8gdds1pbq2blt0t4o08e7.apps.googleusercontent.com';
const PHOTO_UPLOAD_ENABLED = false; // временно выключено - у vision-модели нет резерва, падает при 429/бане ключа
const APP_VERSION = '5.37'; // при каждом деплое поднимать вместе с public/version.json
const ADMIN_EMAIL = 'aegisboostchiron@gmail.com';

const UI_T = {
  ru: {
    welcomeTitle: 'Что прикажете сегодня?',
    newChat: 'Новый чат',
    noChats: 'Нет чатов',
    inputPlaceholder: 'Напиши сообщение...',
    settings: 'Настройки',
    promo: 'Промокод',
    activate: 'Активировать',
    adminPanel: 'Админ-панель',
    copied: 'Скопировано',
    loginTitle: 'Войти в аккаунт',
    login: 'Войти',
    loggingIn: 'Вход...',
    noAccount: '{t.noAccount}',
    registerTitle: 'Создать аккаунт',
    register: 'Зарегистрироваться',
    sending: 'Отправка...',
    haveAccount: '{t.haveAccount}',
    enterCode: 'Введите код из письма',
    codeHint: 'Код действителен 10 минут. Письмо обычно приходит в течение 1-2 минут — если не видите его во «Входящих», проверьте папки «Спам», «Промоакции» и «Оповещения».',
    codePlaceholder: '6-значный код',
    password: 'Пароль',
    verify: 'Подтвердить',
    checking: 'Проверка...',
    modelDesc1: 'Первая модель линейки — простая и быстрая, для базовых задач.',
    modelDesc2: 'Более новая и современная модель — понимает больше и отвечает точнее.',
    modelDesc3: 'Флагманская модель нового поколения — максимальная мощность, лучшая в коде и сложных задачах.',
    attach: 'Прикрепить файл',
    fileTooBig: 'Файл слишком большой (макс. 300 КБ)',
  },
  en: {
    welcomeTitle: 'What are your orders today?',
    newChat: 'New chat',
    noChats: 'No chats yet',
    inputPlaceholder: 'Type a message...',
    settings: 'Settings',
    promo: 'Promo code',
    activate: 'Activate',
    adminPanel: 'Admin panel',
    copied: 'Copied',
    loginTitle: 'Sign in to account',
    login: 'Sign in',
    loggingIn: 'Signing in...',
    noAccount: "No account? Sign up",
    registerTitle: 'Create account',
    register: 'Sign up',
    sending: 'Sending...',
    haveAccount: 'Have an account? Sign in',
    enterCode: 'Enter the code from email',
    codeHint: 'The code is valid for 10 minutes. The email usually arrives within 1-2 minutes — if you don\'t see it in your Inbox, check the Spam and Promotions folders.',
    codePlaceholder: '6-digit code',
    password: 'Password',
    verify: 'Confirm',
    checking: 'Checking...',
    modelDesc1: 'The first model of the lineup — simple and fast, for basic tasks.',
    modelDesc2: 'A newer, more advanced model — understands more and answers better.',
    modelDesc3: 'Flagship next-gen model — maximum power, best at code and complex tasks.',
    attach: 'Attach file',
    fileTooBig: 'File is too big (max 300 KB)',
  },
};

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  tokens?: number;
}

interface User {
  user_id: number;
  name: string;
  email: string;
  tokens_used: number;
  tokens_limit: number;
  remaining: number;
  paid_balance?: number;
  sub_plan?: string | null;
  sub_expires?: string | null;
  referral_code?: string;
  referrals_this_week?: number;
  referrals_total?: number;
  referral_weekly_limit?: number;
  token?: string;
  week_resets_used?: number;
  week_resets_left?: number | null;
  week_reset_at?: string | null;
}

interface ShopConfig {
  price_per_10k: number;
  min_tokens: number;
  max_tokens: number;
  subscriptions: {
    [k: string]: { price: number; limit: number; title: string; description: string };
  };
}

// Правильное склонение "токен/токена/токенов" под число
const pluralTokens = (n: number): string => {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return 'токенов';
  if (last === 1) return 'токен';
  if (last >= 2 && last <= 4) return 'токена';
  return 'токенов';
};

const StarIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
    <path d="M50 5 L54 40 L50 50 L46 40 Z" fill="#D97757"/>
    <path d="M50 95 L54 60 L50 50 L46 60 Z" fill="#D97757"/>
    <path d="M5 50 L40 46 L50 50 L40 54 Z" fill="#D97757"/>
    <path d="M95 50 L60 54 L50 50 L60 46 Z" fill="#D97757"/>
    <path d="M18 18 L45 38 L50 50 L38 45 Z" fill="#D97757"/>
    <path d="M82 82 L55 62 L50 50 L62 55 Z" fill="#D97757"/>
    <path d="M82 18 L62 45 L50 50 L55 38 Z" fill="#D97757"/>
    <path d="M18 82 L38 55 L50 50 L45 62 Z" fill="#D97757"/>
  </svg>
);

// Звезда с лучами по кругу для анимации загрузки: каждый луч по очереди "втягивается"
// к центру со staggered задержкой - создаёт иллюзию вращения без физического поворота формы
const LoadingStarIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" className="loading-star-svg">
    <path className="ray ray-1" d="M50 5 L54 40 L50 50 L46 40 Z" fill="#D97757"/>
    <path className="ray ray-2" d="M82 18 L62 45 L50 50 L55 38 Z" fill="#D97757"/>
    <path className="ray ray-3" d="M95 50 L60 54 L50 50 L60 46 Z" fill="#D97757"/>
    <path className="ray ray-4" d="M82 82 L55 62 L50 50 L62 55 Z" fill="#D97757"/>
    <path className="ray ray-5" d="M50 95 L54 60 L50 50 L46 60 Z" fill="#D97757"/>
    <path className="ray ray-6" d="M18 82 L38 55 L50 50 L45 62 Z" fill="#D97757"/>
    <path className="ray ray-7" d="M5 50 L40 46 L50 50 L40 54 Z" fill="#D97757"/>
    <path className="ray ray-8" d="M18 18 L45 38 L50 50 L38 45 Z" fill="#D97757"/>
  </svg>
);

// Маркеры блока размышлений и блоков-статусов (должны совпадать с backend.py)
const THINK_OPEN = '⟦THINK⟧';
const THINK_CLOSE = '⟦/THINK⟧';
const STEP_OPEN = '⟦STEP⟧';
const STEP_CLOSE = '⟦/STEP⟧';
const STEP_RE = /⟦STEP⟧([\s\S]*?)(?:⟦\/STEP⟧|$)/g;
const POLL_OPEN = '⟦POLL⟧';
const POLL_RE = /⟦POLL⟧([\s\S]*?)(?:⟦\/POLL⟧|$)/g;
const IMG_RE = /⟦IMG⟧([\s\S]*?)(?:⟦\/IMG⟧|$)/g;
// Общий разбор блоков STEP и POLL в одном проходе (порядок в тексте сохраняется)
const BLOCK_RE = /⟦(STEP|POLL|IMG)⟧([\s\S]*?)(?:⟦\/\1⟧|$)/g;

// Иногда модель вместо ⟦POLL⟧/⟦STEP⟧ пишет мусорный префикс вида <tool_call>POLL⟧ —
// чиним такие маркеры перед разбором, чтобы блок всё равно распознался.
function normalizeBlockMarkers(text: string): string {
  if (!text || !text.includes('tool_call')) return text;
  return text
    .replace(/<\/?tool_call>\s*\/(STEP|POLL|IMG)⟧/gi, (_m, kind) => `⟦/${kind}⟧`)
    .replace(/<\/?tool_call>\s*(STEP|POLL|IMG)⟧/gi, (_m, kind) => `⟦${kind}⟧`)
    .replace(/<\/?tool_call>/gi, '');
}

// Убирает блок размышлений и блоки-статусы из текста (для копирования чистого ответа)
function stripThinkFront(content: string): string {
  if (!content) return content;
  let text = normalizeBlockMarkers(content);
  const closeIdx = text.indexOf(THINK_CLOSE);
  if (text.startsWith(THINK_OPEN) && closeIdx !== -1) {
    text = text.slice(closeIdx + THINK_CLOSE.length).trimStart();
  } else if (text.startsWith(THINK_OPEN)) {
    return '';
  }
  // Вырезаем блоки-статусы и опросы целиком
  text = text.replace(STEP_RE, '').replace(POLL_RE, '').replace(IMG_RE, '').replace(/\n{3,}/g, '\n\n').trim();
  return text;
}

// Разбивает текст ответа на сегменты: обычный текст, блоки-статусы (STEP) и опросы (POLL)
type ContentSeg = { type: 'text' | 'step' | 'poll' | 'img'; value: string; streaming?: boolean };
function splitBlocks(content: string): ContentSeg[] {
  const out: ContentSeg[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(BLOCK_RE.source, 'g');
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) out.push({ type: 'text', value: content.slice(last, m.index) });
    const kind: 'step' | 'poll' | 'img' = m[1] === 'STEP' ? 'step' : m[1] === 'POLL' ? 'poll' : 'img';
    const closed = m[0].endsWith(`⟦/${m[1]}⟧`);
    out.push({ type: kind, value: m[2].trim(), streaming: !closed });
    last = m.index + m[0].length;
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  if (last < content.length) out.push({ type: 'text', value: content.slice(last) });
  return out;
}

// Достаём блок размышлений из ответа. streaming=true — блок ещё пишется (нет закрытия)
function parseThink(content: string): { think: string | null; rest: string; streaming: boolean } {
  if (!content || !content.startsWith(THINK_OPEN)) {
    return { think: null, rest: content || '', streaming: false };
  }
  const closeIdx = content.indexOf(THINK_CLOSE);
  if (closeIdx === -1) {
    return { think: content.slice(THINK_OPEN.length), rest: '', streaming: true };
  }
  return {
    think: content.slice(THINK_OPEN.length, closeIdx),
    rest: content.slice(closeIdx + THINK_CLOSE.length),
    streaming: false,
  };
}

// Блок размышлений над ответом. Лейбл «Thought process», кружок-индикатор:
// оранжевый пока модель думает, серый после ответа. По умолчанию свёрнут.
const ThinkBlock = ({ text, streaming, lang }: { text: string; streaming: boolean; lang: 'ru' | 'en' }) => {
  const [open, setOpen] = useState(false);
  const label = lang === 'ru' ? 'Ход мыслей' : 'Thought process';
  return (
    <div className={`think-block ${streaming ? 'streaming' : 'done'}`}>
      <button type="button" className="think-head" onClick={() => setOpen((o) => !o)}>
        <span className={`think-dot ${streaming ? 'active' : ''}`} aria-hidden="true" />
        <span className="think-label">{label}</span>
        <span className={`think-chevron ${open ? 'open' : ''}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </span>
      </button>
      {open && <div className="think-body">{text}</div>}
    </div>
  );
};

// Блок-статус: короткое сообщение о действии модели (сгенерировал файл, отказал и т.п.)
const StepBlock = ({ text, streaming }: { text: string; streaming: boolean }) => (
  <div className={`step-block ${streaming ? 'streaming' : ''}`}>
    <span className="step-icon" aria-hidden="true">
      {streaming ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97757" strokeWidth="2.2" strokeLinecap="round"><path d="M12 3a9 9 0 1 0 9 9" /></svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      )}
    </span>
    <span className="step-text">{text}</span>
  </div>
);

// Виджет опроса: первая строка — вопрос, остальные (2-4) — варианты-кнопки
const PollBlock = ({ raw, onSelect, lang }: { raw: string; onSelect: (opt: string) => void; lang: 'ru' | 'en' }) => {
  const [selected, setSelected] = React.useState<string | null>(null);
  const lines = raw.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length < 2) return null;
  const [question, ...options] = lines;
  return (
    <div className="poll-card">
      <div className="poll-question">{question}</div>
      <div className="poll-options">
        {options.slice(0, 4).map((opt, i) => (
          <button
            key={i}
            type="button"
            className={`poll-option ${selected === opt ? 'selected' : ''}`}
            disabled={!!selected}
            onClick={() => { if (selected) return; setSelected(opt); onSelect(opt); }}
          >
            {selected === opt && (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            )}
            {opt}
          </button>
        ))}
      </div>
      {selected && <div className="poll-answered">{lang === 'ru' ? 'Ответ отправлен' : 'Answer sent'}</div>}
    </div>
  );
};

// Карточка сгенерированного файла: по умолчанию свёрнута (иконка+имя+размер),
// код открывается по тапу и снова прячется по повторному тапу.
const GenFileCard = ({ fname, fcontent, lang, onDownload }: { fname: string; fcontent: string; lang: 'ru' | 'en'; onDownload: () => void }) => {
  const [open, setOpen] = React.useState(false);
  const TRUNC = 20000;
  return (
    <div className="gen-file-card">
      <div className="gen-file-info" onClick={() => setOpen((o) => !o)} role="button" tabIndex={0}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
        <span className="gen-file-name">{fname}</span>
        <span className="gen-file-size">{(fcontent.length / 1024).toFixed(1)} KB</span>
        <svg className={`gen-file-chevron ${open ? 'gen-file-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </div>
      {open && (
        <pre className="gen-file-preview">{fcontent.length > TRUNC ? fcontent.slice(0, TRUNC) + '\n…' : fcontent}</pre>
      )}
      <button className="gen-file-dl" onClick={(e) => { e.stopPropagation(); onDownload(); }}>
        ⬇ {lang === 'ru' ? 'Скачать файл' : 'Download file'}
      </button>
    </div>
  );
};

// Языки, для которых включаем подсветку команд shell
const SHELL_LANGS = new Set(['', 'bash', 'sh', 'shell', 'zsh', 'console', 'terminal', 'cmd', 'powershell', 'ps']);

// Простая построчная подсветка shell-команд
function highlightShellLine(line: string, keyBase: string): React.ReactNode[] {
  if (line.trimStart().startsWith('#')) {
    return [<span key={`${keyBase}c`} className="tok-comment">{line}</span>];
  }
  const tokens = line.split(/(\s+)/);
  let sawCommand = false;
  return tokens.map((tok, i) => {
    const k = `${keyBase}-${i}`;
    if (tok === '' || /^\s+$/.test(tok)) return <span key={k}>{tok}</span>;
    if (/^[|&;()<>]+$/.test(tok)) { sawCommand = false; return <span key={k} className="tok-op">{tok}</span>; }
    if (/^["'].*["']$/.test(tok)) return <span key={k} className="tok-str">{tok}</span>;
    if (tok.startsWith('-')) return <span key={k} className="tok-flag">{tok}</span>;
    if (/^\$/.test(tok)) return <span key={k} className="tok-var">{tok}</span>;
    if (!sawCommand && /^[a-zA-Z0-9._/-]+$/.test(tok)) { sawCommand = true; return <span key={k} className="tok-cmd">{tok}</span>; }
    return <span key={k}>{tok}</span>;
  });
}

const CodeBlock = ({ code, lang, uiLang }: { code: string; lang: string; uiLang: 'ru' | 'en' }) => {
  const [copied, setCopied] = React.useState(false);
  const isShell = SHELL_LANGS.has(lang.toLowerCase());
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = code; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  const lines = code.replace(/\n$/, '').split('\n');
  return (
    <div className="code-card">
      <div className="code-card-head">
        <span className="code-card-lang">{lang ? lang.toLowerCase() : (isShell ? 'shell' : 'code')}</span>
        <button className="code-card-copy" onClick={copy}>
          {copied ? (
            <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>{uiLang === 'ru' ? 'Скопировано' : 'Copied'}</>
          ) : (
            <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>{uiLang === 'ru' ? 'Копировать' : 'Copy'}</>
          )}
        </button>
      </div>
      <pre className="code-card-body">
        <code>
          {lines.map((ln, idx) => (
            <span key={idx} className="code-line">
              {isShell ? highlightShellLine(ln, `l${idx}`) : ln}
              {idx < lines.length - 1 ? '\n' : ''}
            </span>
          ))}
        </code>
      </pre>
    </div>
  );
};

// Плашка-шаг выполнения команды на VPS (как chip'ы прогресса)
const VpsStep = ({ raw }: { raw: string }) => {
  const [open, setOpen] = React.useState(false);
  // Парсим поля: status / label / cmd / output
  const statusM = raw.match(/status:\s*(\w+)/);
  const labelM = raw.match(/label:\s*(.+)/);
  const cmdM = raw.match(/cmd:\s*(.+)/);
  const outM = raw.match(/output:\n([\s\S]*)$/);
  const status = statusM ? statusM[1] : 'done';
  const label = labelM ? labelM[1].trim() : 'Команда';
  const cmd = cmdM ? cmdM[1].trim() : '';
  const output = outM ? outM[1].trim() : '';
  return (
    <div className={`vps-step vps-step-${status}`}>
      <button className="vps-step-head" onClick={() => output && setOpen((o) => !o)}>
        <span className="vps-step-icon" aria-hidden="true">
          {status === 'run' ? (
            <svg className="vps-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 3a9 9 0 1 0 9 9" /></svg>
          ) : status === 'fail' ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          )}
        </span>
        <span className="vps-step-label">{label}</span>
        {cmd && <code className="vps-step-cmd">{cmd}</code>}
        {output && (
          <span className={`vps-step-chevron ${open ? 'open' : ''}`} aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
          </span>
        )}
      </button>
      {open && output && <pre className="vps-step-output">{output}</pre>}
    </div>
  );
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const updateUser = (updater: (prev: User) => User) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      localStorage.setItem('lc_user', JSON.stringify(next));
      return next;
    });
  };
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [thinkingPhrase, setThinkingPhrase] = useState('Still working on it...');
  const longPressTimer = useRef<any>(null);
  const targetTextRef = useRef<string>('');
  const displayedTextRef = useRef<string>('');
  const typingIntervalRef = useRef<any>(null);
  const typingFinalRef = useRef(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  const [genStarted, setGenStarted] = useState(false); // true только после ответа сервера на старт генерации, не сразу по нажатию
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Короткая тактильная вибрация - только по минималкам, не раздражает.
  // navigator.vibrate() внутри WebView часто рапортует успех, но физически не доходит до
  // мотора (WebView-мост ненадёжен) - поэтому в APK используем нативный плагин Capacitor,
  // который обращается к вибрации напрямую через Android API, как это делают нативные приложения.
  const vibrateShort = () => {
    if (Capacitor.isNativePlatform()) {
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    } else {
      try { navigator.vibrate && navigator.vibrate(35); } catch {}
    }
  };
  const [showAdmin, setShowAdmin] = useState(false);
  const [refCopied, setRefCopied] = useState(false);
  const [chatId, setChatId] = useState<number | null>(null);
  const [chatList, setChatList] = useState<any[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  // Голосовой ввод (Web Speech API)
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<any>(null);
  const voiceSupported = Capacitor.isNativePlatform() || (typeof window !== 'undefined' && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition));
  const [starVideoSrc, setStarVideoSrc] = useState('/assets/legends-logo.png');
  const [starIconSrc, setStarIconSrc] = useState('/claude-color.png');

  // Кеш логотипа: скачивается один раз, дальше мгновенно с устройства
  // Автоувеличение высоты поля ввода по мере набора текста (до предела, дальше - скролл внутри)
  useEffect(() => {
    const el = composerInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }, [input]);

  // Загрузка статичной картинки с диска (нативно) или из Cache Storage (веб) -
  // на нативной платформе файл сохраняется в приватное хранилище приложения и
  // при следующих запусках читается БЕЗ обращения к сети вообще.
  const loadPersistentAsset = async (remoteUrl: string, fileName: string, setter: (v: string) => void, onRevoke: (fn: () => void) => void) => {
    if (Capacitor.isNativePlatform()) {
      try {
        const existing = await Filesystem.readFile({ path: fileName, directory: Directory.Data });
        setter(`data:image/png;base64,${existing.data}`);
        return;
      } catch {
        // файла ещё нет на диске - качаем один раз и сохраняем
        try {
          const resp = await fetch(remoteUrl);
          const blob = await resp.blob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Data, recursive: true });
          setter(`data:image/png;base64,${base64}`);
        } catch { /* сеть недоступна - остаётся дефолтный src по сети как раньше */ }
      }
      return;
    }
    // Веб-версия: как и раньше, через Cache Storage
    try {
      if (!('caches' in window)) return;
      const cache = await caches.open('lc-assets-v1');
      let resp = await cache.match(remoteUrl);
      if (!resp) {
        await cache.add(remoteUrl);
        resp = await cache.match(remoteUrl);
      }
      if (resp) {
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        onRevoke(() => URL.revokeObjectURL(objectUrl));
        setter(objectUrl);
      }
    } catch { /* кеш недоступен - грузим по сети как раньше */ }
  };

  useEffect(() => {
    let revokeFn: (() => void) | null = null;
    loadPersistentAsset('/assets/legends-logo.png', 'legends-logo.png', setStarVideoSrc, (fn) => { revokeFn = fn; });
    return () => { if (revokeFn) revokeFn(); };
  }, []);

  // Кеш иконки звезды (используется в ответах нейросети и на экране входа).
  // На нативной платформе - постоянное хранилище на диске (Filesystem), не сеть при каждом запуске.
  useEffect(() => {
    let revokeFn: (() => void) | null = null;
    loadPersistentAsset('/claude-color.png', 'claude-color.png', setStarIconSrc, (fn) => { revokeFn = fn; });
    return () => { if (revokeFn) revokeFn(); };
  }, []);

  // Auth states
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'verify'>('login');
  const [captchaToken, setCaptchaToken] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const captchaWidgetId = useRef<number | null>(null);

  useEffect(() => {
    if (authMode !== 'register') return;
    setCaptchaToken('');
    const iv = setInterval(() => {
      const grecaptcha = (window as any).grecaptcha;
      const el = document.getElementById('recaptcha-container');
      if (grecaptcha && grecaptcha.render && el && captchaWidgetId.current === null) {
        captchaWidgetId.current = grecaptcha.render(el, {
          sitekey: '6LfeIFktAAAAAHS9HwLS7_KqG2YS40L-P_jCrz8T',
          theme: 'dark',
          callback: (t: string) => setCaptchaToken(t),
          'expired-callback': () => setCaptchaToken(''),
        });
        clearInterval(iv);
      }
    }, 300);
    return () => clearInterval(iv);
  }, [authMode]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [tempPasswordHash, setTempPasswordHash] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Promo code
  const [promoCode, setPromoCode] = useState('');
  const [promoMsg, setPromoMsg] = useState('');

  // Shop
  const [showShop, setShowShop] = useState(false);
  const [showLimits, setShowLimits] = useState(false);
  const [shopConfig, setShopConfig] = useState<ShopConfig | null>(null);
  const [buyTokens, setBuyTokens] = useState(30000);
  const [shopMsg, setShopMsg] = useState('');
  const [shopLoading, setShopLoading] = useState(false);
  const [pendingLabel, setPendingLabel] = useState<string | null>(() => localStorage.getItem('lc_pending_payment'));
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [outOfTokens, setOutOfTokens] = useState(false);
  const [chatBlocked, setChatBlocked] = useState(false);

  // Admin
  const [adminStats, setAdminStats] = useState<any>(null);
  const [adminLogs, setAdminLogs] = useState<any[]>([]);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<{ [msgIdx: number]: 'like' | 'dislike' }>({});
  const [dislikeCommentFor, setDislikeCommentFor] = useState<number | null>(null);
  const [dislikeCommentText, setDislikeCommentText] = useState('');
  const [supportMessages, setSupportMessages] = useState<any[]>([]);
  const [supportInput, setSupportInput] = useState('');
  const [supportSending, setSupportSending] = useState(false);
  const [supportUnread, setSupportUnread] = useState(false);
  const [adminSupportChats, setAdminSupportChats] = useState<any[]>([]);
  const [adminSupportSelected, setAdminSupportSelected] = useState<number | null>(null);
  const [adminSupportDetail, setAdminSupportDetail] = useState<any>(null);
  const [adminSupportReply, setAdminSupportReply] = useState('');
  const [adminSupportMenuFor, setAdminSupportMenuFor] = useState<number | null>(null);
  const [adminGrantAmount, setAdminGrantAmount] = useState('10000');
  const supportPressTimer = useRef<any>(null);
  const [showErrorLogsOnly, setShowErrorLogsOnly] = useState(false);
  const [keyTokens, setKeyTokens] = useState('100000');
  const [keyActivations, setKeyActivations] = useState('1');
  const [keyExpireDays, setKeyExpireDays] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [keyCopied, setKeyCopied] = useState(false);
  const [adminTab, setAdminTab] = useState<'stats' | 'keys' | 'openrouter' | 'logs' | 'payments' | 'prices' | 'support' | 'feedback'>('stats');
  const [adminFeedback, setAdminFeedback] = useState<any[]>([]);
  const [adminFeedbackCounts, setAdminFeedbackCounts] = useState<{ likes: number; dislikes: number }>({ likes: 0, dislikes: 0 });
  const [adminFeedbackFilter, setAdminFeedbackFilter] = useState<'all' | 'like' | 'dislike'>('all');
  const [adminPayments, setAdminPayments] = useState<any>(null);
  const [adminPrices, setAdminPrices] = useState<any>({});
  const [pricesMsg, setPricesMsg] = useState('');
  const [selectedModel, setSelectedModel] = useState<'main' | 'mini' | 'v2'>('v2');
  // Админ: ключи OpenRouter
  const [v0Keys, setV0Keys] = useState<any[]>([]);
  const [keyProvider, setKeyProvider] = useState<'openrouter' | 'glm' | 'groq'>('openrouter');
  const [newV0Key, setNewV0Key] = useState('');
  const [newV0Label, setNewV0Label] = useState('');
  const [v0KeysMsg, setV0KeysMsg] = useState('');
  const [v0KeysChecking, setV0KeysChecking] = useState(false);
  const [thinkingEnabled, setThinkingEnabled] = useState(() => localStorage.getItem('lc_thinking') === '1');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateSize, setUpdateSize] = useState('');
  const [updateNotes, setUpdateNotes] = useState<string[]>([]);
  const [updateStage, setUpdateStage] = useState<'prompt' | 'installing' | 'done'>('prompt');
  const [updateProgress, setUpdateProgress] = useState(0);

  const startUpdateInstall = () => {
    setUpdateStage('installing');
    setUpdateProgress(0);
    const started = Date.now();
    const duration = 1800; // "меньше минуты", реально - секунды
    const tick = () => {
      const pct = Math.min(100, Math.round(((Date.now() - started) / duration) * 100));
      setUpdateProgress(pct);
      if (pct < 100) {
        requestAnimationFrame(tick);
      } else {
        setUpdateStage('done');
      }
    };
    requestAnimationFrame(tick);
  };

  // Скрываем нативный сплэш APK, как только интерфейс загрузился
  useEffect(() => {
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) {
      // Отступ под системную строку состояния: WebView рисуется на весь экран
      document.body.classList.add('native-app');
      import('@capacitor/splash-screen')
        .then(({ SplashScreen }) => SplashScreen.hide())
        .catch(() => {});
    }
  }, []);

  // Проверка новой версии на сервере: если вышла обнова - показываем полноэкранный блок,
  // который нельзя пропустить (пока изменения не всегда безопасно оставлять на потом -
  // старый JS может разойтись с новым API бэкенда).
  useEffect(() => {
    let stopped = false;
    let justUpdated = false;
    try {
      justUpdated = sessionStorage.getItem('lc_just_updated') === '1';
      sessionStorage.removeItem('lc_just_updated');
    } catch {}
    // Если мы только что перезагрузились после нажатия "Обновить" - даём сети/кэшу немного
    // времени устаканиться и тихо перепроверяем несколько раз, вместо того чтобы сразу
    // снова показывать резкий полноэкранный экран (иначе похоже на зацикливание).
    let retriesLeft = justUpdated ? 4 : 0;
    let lastCheckAt = 0;
    const MIN_GAP_MS = 20000; // минимум 20с между проверками, что бы ни было триггером
    const checkVersion = async (force?: boolean) => {
      const now = Date.now();
      if (!force && now - lastCheckAt < MIN_GAP_MS) return; // MIUI WebView может слать visibilitychange очень часто - не долбим сервер и не дёргаем юзера
      lastCheckAt = now;
      try {
        const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (stopped || !data.version) return;
        if (data.version !== APP_VERSION) {
          if (retriesLeft > 0) {
            retriesLeft -= 1;
            setTimeout(() => checkVersion(true), 1500);
            return;
          }
          if (data.size_mb) setUpdateSize(`${data.size_mb} МБ`);
          if (Array.isArray(data.notes)) setUpdateNotes(data.notes);
          setUpdateAvailable(true);
        }
      } catch {}
    };
    checkVersion(true);
    const iv = setInterval(() => checkVersion(), 120000); // каждые 2 минуты
    const onVisible = () => { if (document.visibilityState === 'visible') checkVersion(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stopped = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  // Применение обновления: чистим кеш оболочки, чтобы подтянулась свежая версия, и перезагружаемся
  const applyUpdate = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      navigator.serviceWorker?.controller?.postMessage('lc-clear-cache');
    } catch {}
    try { sessionStorage.setItem('lc_just_updated', '1'); } catch {}
    // Обычный reload() иногда не форсирует настоящий обход кэша (особенно в WebView APK) -
    // переходим на новый URL с уникальным параметром, чтобы гарантированно получить свежую сеть.
    const bust = (window.location.pathname.includes('?') ? '&' : '?') + '_upd=' + Date.now();
    window.location.href = window.location.origin + window.location.pathname + bust;
  };

  // Скачивание файла, сгенерированного моделью: Blob создаётся на устройстве, сервер не участвует
  const downloadGeneratedFile = async (name: string, content: string) => {
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) {
      // Android WebView не умеет скачивать blob-ссылки — сохраняем через нативные плагины
      try {
        const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
        const res = await Filesystem.writeFile({
          path: name,
          data: content,
          directory: Directory.Cache,
          encoding: Encoding.UTF8,
        });
        const { Share } = await import('@capacitor/share');
        await Share.share({
          title: name,
          url: res.uri,
        });
        return;
      } catch (err) {
        console.log('[v0] native save failed, falling back:', err);
      }
    }
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  // Рендер обычного текста: блоки ```file:имя ...``` превращаются в карточки со скачиванием
  // Плейсхолдер, пока код-блок ещё пишется (закрывающих ``` ещё не пришло)
  const renderGeneratingPlaceholder = (openTag: string, keyBase: string, bodyLen: number = 0) => {
    const tag = openTag.trim();
    const fname = tag.startsWith('file:') ? tag.slice(5).trim() : null;
    const sizeLabel = bodyLen > 0
      ? (bodyLen >= 1024 ? ` (${(bodyLen / 1024).toFixed(1)} КБ)` : ` (${bodyLen} симв.)`)
      : '';
    const label = fname
      ? (uiLang === 'ru' ? `Собираю файл «${fname}»…${sizeLabel}` : `Generating file "${fname}"…${sizeLabel}`)
      : (uiLang === 'ru' ? `Собираю код…${sizeLabel}` : `Generating code…${sizeLabel}`);
    return (
      <div key={`${keyBase}gen`} className="gen-file-generating">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#D97757" strokeWidth="2.2" strokeLinecap="round" className="gen-file-generating-spin">
          <path d="M12 3a9 9 0 1 0 9 9" />
        </svg>
        <span>{label}</span>
      </div>
    );
  };

  // Рендерит один fenced-блок (код/файл/vps-режим) по тегу и телу - переиспользуется
  // и для полностью пришедших блоков, и для "дозакрытых" в конце сообщения.
  const renderFenceByTag = (tag: string, body: string, key: string): React.ReactNode => {
    if (tag === 'vps-step') return <VpsStep key={key} raw={body} />;
    if (tag === 'vps-exec' || tag === 'vps-output') {
      return (
        <div key={key} className="vps-terminal">
          <div className="vps-terminal-head">
            <span className="vps-terminal-dot"></span>
            {tag === 'vps-exec' ? 'VPS — команды' : 'VPS — вывод сервера'}
          </div>
          <pre className="vps-terminal-body">{body.trim()}</pre>
        </div>
      );
    }
    if (!tag.startsWith('file:')) {
      return <CodeBlock key={key} code={body} lang={tag} uiLang={uiLang} />;
    }
    const fname = tag.slice(5).trim();
    return (
      <GenFileCard
        key={key}
        fname={fname}
        fcontent={body}
        lang={uiLang}
        onDownload={() => downloadGeneratedFile(fname, body)}
      />
    );
  };

  const renderTextWithFiles = (content: string, keyBase: string, isStreaming: boolean = false): React.ReactNode => {
    // Ловим все fenced-блоки: ```file:имя, ```vps-exec/output и обычные ```lang
    const re = /```([^\n`]*)\n([\s\S]*?)```/g;
    const parts: React.ReactNode[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    let i = 0;
    while ((m = re.exec(content)) !== null) {
      if (m.index > last) parts.push(<span key={`${keyBase}t${i}`}>{content.slice(last, m.index)}</span>);
      const tag = m[1].trim();
      parts.push(renderFenceByTag(tag, m[2], `${keyBase}b${i}`));
      last = m.index + m[0].length;
      i++;
    }
    // Хвост после последнего закрытого блока: проверяем, не открыт ли новый код-блок
    const tail = content.slice(last);
    const openMatch = tail.match(/```([^\n`]*)\n([\s\S]*)$/);
    if (openMatch) {
      const before = tail.slice(0, openMatch.index);
      // Если генерация уже реально завершилась (не стримится) - не вечно ждём закрытия,
      // а показываем то что пришло как обычный готовый блок (модель могла оборваться на лимите токенов)
      const fenceNode = isStreaming
        ? renderGeneratingPlaceholder(openMatch[1], keyBase, openMatch[2].length)
        : renderFenceByTag(openMatch[1].trim(), openMatch[2], `${keyBase}tail`);
      if (i === 0) {
        return (
          <>
            {before && <span key={`${keyBase}tb`}>{before}</span>}
            {fenceNode}
          </>
        );
      }
      if (before) parts.push(<span key={`${keyBase}t${i}`}>{before}</span>);
      parts.push(fenceNode);
      return <>{parts}</>;
    }
    if (i === 0) return content;
    if (last < content.length) parts.push(<span key={`${keyBase}t${i}`}>{content.slice(last)}</span>);
    return <>{parts}</>;
  };

  // Разбор ответа: блок размышлений сверху, затем текст с блоками-статусами (STEP) и файлами
  const renderAssistantContent = (rawRaw: string, isStreaming: boolean = false) => {
    const rawContent = normalizeBlockMarkers(rawRaw);
    const { think, rest, streaming } = parseThink(rawContent);
    const thinkNode = think !== null && think.trim() !== ''
      ? <ThinkBlock key="think" text={think.trim()} streaming={streaming} lang={uiLang} />
      : null;
    const content = rest;
    if (think !== null && content === '') {
      // Пока пишется только рассуждение — показываем один блок размышлений
      return <>{thinkNode}</>;
    }

    // Если блоков-статусов, опросов и картинок нет — быстрый путь
    if (!content.includes(STEP_OPEN) && !content.includes(POLL_OPEN) && !content.includes('⟦IMG⟧')) {
      return <>{thinkNode}{renderTextWithFiles(content, 'c', isStreaming)}</>;
    }

    const segs = splitBlocks(content);
    return (
      <>
        {thinkNode}
        {segs.map((seg, idx) => {
          if (seg.type === 'step') {
            return seg.value ? <StepBlock key={`s${idx}`} text={seg.value} streaming={!!seg.streaming} /> : null;
          }
          if (seg.type === 'poll') {
            if (seg.streaming || !seg.value) return null; // ждём полного блока перед рендером кнопок
            return (
              <PollBlock
                key={`p${idx}`}
                raw={seg.value}
                lang={uiLang}
                onSelect={(opt) => { if (!loading) sendMessage(opt); }}
              />
            );
          }
          if (seg.type === 'img') {
            if (seg.streaming || !seg.value) return null; // ждём полного пути перед показом
            return (
              <a key={`i${idx}`} href={seg.value} target="_blank" rel="noopener noreferrer" className="browse-shot-link">
                <img src={seg.value} alt="Скриншот страницы" className="browse-shot-img" loading="lazy" />
              </a>
            );
          }
          return <React.Fragment key={`x${idx}`}>{renderTextWithFiles(seg.value, `x${idx}`, isStreaming)}</React.Fragment>;
        })}
      </>
    );
  };
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showSplash, setShowSplash] = useState(false);
  const [uiLang, setUiLang] = useState<'ru' | 'en'>(() => (localStorage.getItem('uiLang') as 'ru' | 'en') || 'ru');
  const [bgColor, setBgColor] = useState<string>(() => localStorage.getItem('bgColor') || '#1a1a1a');
  const applyBgColor = (color: string) => { setBgColor(color); localStorage.setItem('bgColor', color); };
  const t = UI_T[uiLang];
  const [welcomeTyped, setWelcomeTyped] = useState('');
  const [welcomeFull, setWelcomeFull] = useState('');
  const [attachedFile, setAttachedFile] = useState<{ name: string; content: string } | null>(null);
  const [attachedImages, setAttachedImages] = useState<string[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    document.documentElement.style.setProperty('--bg-color', bgColor);
    document.body.style.background = bgColor;
  }, [bgColor]);
  useEffect(() => {
    if (messages.length !== 0) return;
    setWelcomeTyped('');
    const phrasesRu = [
      'Что прикажете сегодня?',
      'С чего начнём?',
      'Какая задача сегодня?',
      'Чем займёмся?',
      'Готов к работе — что делаем?',
      'Слушаю, что нужно сделать?',
    ];
    const phrasesEn = [
      'What are your orders today?',
      'Where should we start?',
      'What do you need today?',
      'What are we building?',
      'Ready to work — what\'s the task?',
      'Listening — what\'s up?',
    ];
    const pool = uiLang === 'ru' ? phrasesRu : phrasesEn;
    const full = pool[Math.floor(Math.random() * pool.length)];
    setWelcomeFull(full);
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setWelcomeTyped(full.slice(0, i));
      if (i >= full.length) clearInterval(timer);
    }, 70);
    return () => clearInterval(timer);
  }, [messages.length, uiLang]);
  const addImageFiles = (files: File[]) => {
    const room = 5 - attachedImages.length;
    if (room <= 0) { alert(uiLang === 'ru' ? 'Максимум 5 фото за раз' : 'Max 5 photos at once'); return; }
    files.slice(0, room).forEach((f) => {
      if (f.size > 5 * 1024 * 1024) { alert(uiLang === 'ru' ? `Фото ${f.name} больше 5 МБ` : `${f.name} is over 5MB`); return; }
      const reader = new FileReader();
      reader.onload = () => {
        setAttachedImages((prev) => (prev.length < 5 ? [...prev, String(reader.result || '')] : prev));
      };
      reader.readAsDataURL(f);
    });
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (f.type.startsWith('image/')) {
      if (!PHOTO_UPLOAD_ENABLED) { alert(uiLang === 'ru' ? 'Обработка фото временно недоступна, скоро вернём' : 'Photo processing temporarily unavailable'); return; }
      addImageFiles([f]); return;
    }
    if (f.size > 300 * 1024) { alert(t.fileTooBig); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setAttachedFile({ name: f.name, content: String(reader.result || '').slice(0, 200000) });
    };
    reader.readAsText(f);
  };

  const handleImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    e.target.value = '';
    if (files.length) addImageFiles(files);
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContentRef = useRef<HTMLDivElement>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  // Прилипаем к низу только когда пользователь уже внизу. Если он сам ушёл вверх — не тянем обратно.
  const stickToBottomRef = useRef(true);
  const isNearBottom = () => {
    const el = chatContentRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  // Прокрутка от самого пользователя (wheel/touch): сразу решаем, прилипать или нет.
  // Не трогаем stick на программной прокрутке — иначе автоскролл сам себя перебивает.
  const handleUserScrollIntent = (deltaUp: boolean) => {
    if (deltaUp) {
      stickToBottomRef.current = false;      // повёл вверх — отцепляемся
      setShowScrollDown(true);
    } else if (isNearBottom()) {
      stickToBottomRef.current = true;       // вернулся к низу — снова прилипаем
      setShowScrollDown(false);
    }
  };
  // Доп. подстраховка на инерционный скролл (моб. свайп без wheel/touchmove-событий на каждый кадр)
  const onChatScroll = () => {
    if (isNearBottom()) {
      stickToBottomRef.current = true;
      setShowScrollDown(false);
    } else if (!stickToBottomRef.current) {
      setShowScrollDown(true);
    }
  };
  const wheelHandler = (e: React.WheelEvent) => handleUserScrollIntent(e.deltaY < 0);
  const touchStartY = useRef(0);
  const onTouchStart = (e: React.TouchEvent) => { touchStartY.current = e.touches[0].clientY; };
  const onTouchMove = (e: React.TouchEvent) => {
    const dy = e.touches[0].clientY - touchStartY.current;
    touchStartY.current = e.touches[0].clientY;
    handleUserScrollIntent(dy > 0); // палец вниз = контент вверх = скролл вверх
  };

  // Отпечаток устройства: canvas-рендер + характеристики системы, для ограничения "1 аккаунт на устройство"
  const getDeviceFingerprint = (): string => {
    try {
      let canvasSig = '';
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.textBaseline = 'top';
          ctx.font = '14px Arial';
          ctx.fillText('lc-fingerprint-🔑', 2, 2);
          canvasSig = canvas.toDataURL();
        }
      } catch {}
      const parts = [
        navigator.userAgent,
        navigator.language,
        String(window.screen.width) + 'x' + String(window.screen.height),
        String(window.screen.colorDepth),
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        String(navigator.hardwareConcurrency || ''),
        canvasSig,
      ].join('|');
      let hash = 0;
      for (let i = 0; i < parts.length; i++) {
        hash = (hash * 31 + parts.charCodeAt(i)) | 0;
      }
      return 'fp_' + Math.abs(hash).toString(36) + '_' + parts.length;
    } catch {
      return 'fp_unknown';
    }
  };

  const handleRegister = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/register`, { email, password, captcha_token: captchaToken, agree_terms: agreeTerms });
      setTempPasswordHash(res.data.temp_password);
      setAuthMode('verify');
    } catch (err: any) {
      setAuthError(err.response?.data?.detail || 'Ошибка регистрации');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerify = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/verify`, {
        email,
        code: verifyCode,
        temp_password: tempPasswordHash,
        device_fingerprint: getDeviceFingerprint(),
      });
      const u = res.data;
      setUser(u);
      localStorage.setItem('lc_user', JSON.stringify(u));
      setShowSplash(true);
      setTimeout(() => setShowSplash(false), 1500);
    } catch (err: any) {
      setAuthError(err.response?.data?.detail || 'Ошибка подтверждения');
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) return;
    // Режим размышлений (V2 + thinking) показывает "Thought process...", обычный - "Still working on it..."
    const phrase = selectedModel === 'v2' && thinkingEnabled ? 'Thought process...' : 'Still working on it...';
    setThinkingPhrase(phrase);
  }, [loading, selectedModel, thinkingEnabled]);

  useEffect(() => {
    const saved = localStorage.getItem('lc_user');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setUser(parsed);
        fetch(`${API_URL}/user/${parsed.user_id}?token=${encodeURIComponent(parsed.token || '')}`, { method: 'GET' })
          .then((resp) => {
            if (resp.status === 403) {
              // Старая сессия без токена (или невалидная) - просим войти заново
              localStorage.removeItem('lc_user');
              setUser(null);
              return null;
            }
            return resp.ok ? resp.json() : null;
          })
          .then((updatedUser) => {
            if (updatedUser) {
              setUser(updatedUser);
              localStorage.setItem('lc_user', JSON.stringify(updatedUser));
            }
          })
          .catch(() => {});
      } catch {}
    }
  }, []);

  const refreshUserData = async () => {
    if (!user) return;
    try {
      const resp = await fetch(`${API_URL}/user/${user.user_id}?token=${encodeURIComponent(user.token || '')}`, { method: 'GET' });
      if (resp.status === 403) {
        localStorage.removeItem('lc_user');
        setUser(null);
        return;
      }
      if (resp.ok) {
        const updatedUser = await resp.json();
        setUser(updatedUser);
        localStorage.setItem('lc_user', JSON.stringify(updatedUser));
      }
    } catch (err) { console.error('Failed to refresh user data:', err); }
  };
  const googleInitedRef = useRef(false);
  const handleGoogleLogin = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      if (!googleInitedRef.current) {
        await SocialLogin.initialize({
          google: { webClientId: GOOGLE_WEB_CLIENT_ID },
        });
        googleInitedRef.current = true;
      }
      const res = await SocialLogin.login({
        provider: 'google',
        options: {},
      });
      const idToken = (res as any)?.result?.idToken;
      if (!idToken) {
        setAuthError('Не удалось получить данные от Google, попробуй ещё раз.');
        setAuthLoading(false);
        return;
      }
      const backendRes = await axios.post(`${API_URL}/auth/google/verify`, { id_token: idToken });
      setUser(backendRes.data);
      localStorage.setItem('lc_user', JSON.stringify(backendRes.data));
      setShowSplash(true);
      setTimeout(() => setShowSplash(false), 1500);
    } catch (err: any) {
      const code = err?.code || err?.errorCode;
      const rawMessage = err?.message || err?.errorMessage || '';
      if (code === 'USER_CANCELLED' || /cancel/i.test(rawMessage)) {
        setAuthError('Вход отменён.');
      } else {
        const detail = err?.response?.data?.detail || rawMessage || (typeof err === 'string' ? err : JSON.stringify(err));
        console.error('Google login error:', err);
        setAuthError(`Не удалось войти через Google: ${detail}`);
      }
    } finally {
      setAuthLoading(false);
    }
  };
  const handleLogin = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/login`, { email, password });
      setUser(res.data);
      localStorage.setItem('lc_user', JSON.stringify(res.data));
      setShowSplash(true);
      setTimeout(() => setShowSplash(false), 1500);
    } catch (err: any) {
      setAuthError(err.response?.data?.detail || 'Ошибка входа');
    } finally {
      setAuthLoading(false);
    }
  };



  const isNetworkError = (err: any) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
    const msg = String(err?.message || '');
    // ВАЖНО: не проверяем "err instanceof TypeError" в общем виде - TypeError бывает от любого
    // не связанного с сетью бага в коде (например, обращение к несуществующему свойству), и раньше
    // это ошибочно принималось за "нет интернета". Смотрим только на реальные сетевые формулировки.
    return /failed to fetch|network ?error|load failed/i.test(msg);
  };

  const sendMessage = async (overrideText?: string) => {
    const sourceText = overrideText !== undefined ? overrideText : input;
    if ((!sourceText.trim() && !attachedFile && attachedImages.length === 0) || !user || loading || chatBlocked) return;
    let userMessage = sourceText.trim();
    if (attachedFile) {
      userMessage = (userMessage + '\n\n[Файл: ' + attachedFile.name + ']\n```\n' + attachedFile.content + '\n```').trim();
      setAttachedFile(null);
    }
    const imagesToSend = attachedImages;
    setAttachedImages([]);
    setInput('');
    targetTextRef.current = '';
    displayedTextRef.current = '';
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    typingFinalRef.current = false;
    stickToBottomRef.current = true; // новое сообщение — снова прилипаем к низу
    setShowScrollDown(false);
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }, { role: 'assistant', content: '' }]);
    setLoading(true);
    setGenStarted(false);
    try {
      const resp = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.user_id,
          token: user.token,
          message: userMessage,
          images: imagesToSend,
          chat_id: chatId,
          model: selectedModel === 'mini' ? 'nvidia/nemotron-3-super-120b-a12b:free' : undefined,
          model_choice: selectedModel === 'v2' ? 'v2' : 'v1',
          thinking: selectedModel === 'v2' && thinkingEnabled,
          web_search: webSearchEnabled,
        }),
      });
      if (!resp.ok) {
        let errText = await resp.text().catch(() => 'Ошибка запроса');
        try {
          const parsed = JSON.parse(errText);
          if (parsed.detail) errText = parsed.detail;
        } catch {}
        if (resp.status === 429 && errText.includes('закончились токены')) {
          setOutOfTokens(true);
        }
        if (errText.includes('__CHAT_BLOCKED__')) {
          setChatBlocked(true);
          setMessages((prev) => prev.slice(0, -1)); // убираем пустой плейсхолдер ответа ассистента
          setLoading(false);
          return;
        }
        throw new Error(errText);
      }
      const startData = await resp.json();
      const activeChatId = startData.chat_id;
      setChatId(activeChatId);

      if (startData.status !== 'generating') {
        let errorMsg = startData.text || startData.response || 'Модель не смогла обработать запрос';
        if (startData.status === 'error') { errorMsg = startData.error || 'Ошибка: попробуйте еще раз'; }
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: errorMsg };
          return updated;
        });
        if (startData.tokens_used) {
          updateUser((prev) => ({ ...prev, tokens_used: prev.tokens_used + startData.tokens_used, remaining: Math.max(0, prev.remaining - startData.tokens_used) }));
        }
        setLoading(false);
        return;
      }
      setGenStarted(true);

      let pollRetriedOnce = false;
      const poll = async () => {
        try {
          const statusResp = await fetch(`${API_URL}/chat/${activeChatId}/status?_t=${Date.now()}&user_id=${user.user_id}&token=${encodeURIComponent(user.token || '')}`, { cache: 'no-store' });
          if (!statusResp.ok) {
            setLoading(false);
            refreshUserData();
            return;
          }
          const statusData = await statusResp.json();
          targetTextRef.current = statusData.text || '';
          if (!typingIntervalRef.current) {
            typingIntervalRef.current = setInterval(() => {
              const target = targetTextRef.current;
              const shown = displayedTextRef.current;
              if (shown.length < target.length) {
                const step = Math.max(1, Math.ceil((target.length - shown.length) / 20));
                displayedTextRef.current = target.slice(0, shown.length + step);
                const currentText = displayedTextRef.current;
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: currentText };
                  return updated;
                });
              }
              if (shown.length >= target.length && typingFinalRef.current) {
                clearInterval(typingIntervalRef.current);
                typingIntervalRef.current = null;
              }
            }, 12);
          }
          if (statusData.done) {
            typingFinalRef.current = true;
            vibrateShort();
            if (statusData.meta) {
              updateUser((prevUser) => ({
                ...prevUser,
                remaining: statusData.meta.remaining,
                tokens_used: prevUser.tokens_used + statusData.meta.tokens_used,
              }));
            }
            setLoading(false);
            loadChatHistory();
            setTimeout(() => refreshUserData(), 1000);
            return;
          }
          setTimeout(poll, 400);
        } catch (err) {
          if (!loadingRef.current) return; // ответ уже пришёл и завершился успешно - это запоздалый вызов, не мешаем готовому результату
          // Один тихий повтор перед тем, как беспокоить юзера: мобильные браузеры троттлят
          // таймеры в фоне (когда приложение свёрнуто/потеряло фокус), это может дать
          // ложное срабатывание. Настоящий обрыв сети не пройдёт и повторную попытку.
          if (!pollRetriedOnce) {
            pollRetriedOnce = true;
            setTimeout(poll, 800);
            return;
          }
          setLoading(false);
          setMessages((prev) => {
            const updated = [...prev];
            const lastText = updated[updated.length - 1]?.content || '';
            updated[updated.length - 1] = {
              role: 'assistant',
              content: lastText + (lastText ? '\n\n' : '') + `❌  ${isNetworkError(err) ? 'Проверьте соединение с интернетом' : 'Ошибка соединения, попробуйте ещё раз'}`,
            };
            return updated;
          });
        }
      };
      poll();
    } catch (error: any) {
      const errorMsg = isNetworkError(error) ? 'Проверьте соединение с интернетом' : (error.message || 'Ошибка');
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: `❌  ${errorMsg}` };
        return updated;
      });
      setLoading(false);
    }
  };
  // Нативное распознавание (APK): плагин Capacitor. Браузер: Web Speech API.
  const startNativeVoice = async () => {
    try {
      const { available } = await NativeSpeech.available();
      if (!available) {
        alert(uiLang === 'ru' ? 'Распознавание речи недоступно на этом устройстве' : 'Speech recognition unavailable on this device');
        return;
      }
      const perm = await NativeSpeech.requestPermissions();
      if (perm.speechRecognition !== 'granted') {
        alert(uiLang === 'ru' ? 'Разреши доступ к микрофону в настройках приложения' : 'Allow microphone access in app settings');
        return;
      }
      const baseText = input ? input + ' ' : '';
      await NativeSpeech.removeAllListeners();
      NativeSpeech.addListener('partialResults', (data: any) => {
        const t = data?.matches?.[0];
        if (t) setInput(baseText + t);
      });
      NativeSpeech.addListener('listeningState', (data: any) => {
        if (data?.status === 'stopped') {
          setIsRecording(false);
          NativeSpeech.removeAllListeners();
        }
      });
      setIsRecording(true);
      await NativeSpeech.start({
        language: uiLang === 'ru' ? 'ru-RU' : 'en-US',
        partialResults: true,
        popup: false,
      });
    } catch (e) {
      console.error('[voice] native error', e);
      setIsRecording(false);
      try { await NativeSpeech.removeAllListeners(); } catch {}
    }
  };

  const toggleVoiceInput = async () => {
    if (isRecording) {
      if (Capacitor.isNativePlatform()) {
        try { await NativeSpeech.stop(); } catch {}
        setIsRecording(false);
      } else {
        recognitionRef.current?.stop();
      }
      return;
    }
    if (Capacitor.isNativePlatform()) {
      await startNativeVoice();
      return;
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      alert(uiLang === 'ru' ? 'Голосовой ввод не поддерживается в этом браузере' : 'Voice input is not supported in this browser');
      return;
    }
    const rec = new SR();
    rec.lang = uiLang === 'ru' ? 'ru-RU' : 'en-US';
    rec.interimResults = true;
    rec.continuous = false;
    const baseText = input ? input + ' ' : '';
    rec.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput(baseText + transcript);
    };
    rec.onend = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };
    rec.onerror = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };
    recognitionRef.current = rec;
    setIsRecording(true);
    rec.start();
  };

  const stopGeneration = async () => {
    if (!chatId || !user) return;
    try {
      await fetch(`${API_URL}/chat/${chatId}/stop?user_id=${user.user_id}&token=${encodeURIComponent(user.token || '')}`, { method: 'POST' });
      // Полинг сам получит done=true и корректно завершит: частичный ответ сохранится
    } catch {}
  };

  const loadChatHistory = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/history/${user.user_id}`, { params: { token: user.token } });
      setChatList(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const openChat = async (id: number) => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/chat/${id}/messages`, { params: { user_id: user.user_id, token: user.token } });
      const filtered = res.data.messages.filter((m: Message) => m.role !== 'system');
      setMessages(filtered);
      setChatId(id);
      setChatBlocked(!!res.data.blocked);
      const chatMeta = chatList.find((c) => c.id === id);
      const mc = chatMeta?.model_choice;
      if (mc === 'v2') setSelectedModel('v2');
      else if (mc === 'v1') setSelectedModel('main');
      setSidebarOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setChatId(null);
    setChatBlocked(false);
    setSidebarOpen(false);
  };

  const confirmDeleteChat = async (id: number) => {
    if (!user) return;
    try {
      await axios.delete(`${API_URL}/chat/${id}`, { params: { user_id: user.user_id, token: user.token } });
      setDeleteConfirmId(null);
      if (chatId === id) {
        setMessages([]);
        setChatId(null);
      }
      loadChatHistory();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteAllChats = async () => {
    if (!user) return;
    // Первый тап - просим подтвердить, второй - удаляем
    if (!deleteAllConfirm) {
      setDeleteAllConfirm(true);
      setTimeout(() => setDeleteAllConfirm(false), 5000);
      return;
    }
    try {
      await axios.delete(`${API_URL}/chats/all`, { params: { user_id: user.user_id, token: user.token } });
      setDeleteAllConfirm(false);
      setMessages([]);
      setChatId(null);
      setChatList([]);
      loadChatHistory();
    } catch (err) {
      console.error(err);
      setDeleteAllConfirm(false);
    }
  };
  const redeemPromo = async () => {
    if (!user || !promoCode.trim()) return;
    setPromoMsg('');
    try {
      const res = await axios.post(`${API_URL}/promo/redeem`, {
        user_id: user.user_id,
        token: user.token,
        key_code: promoCode.trim(),
      });
      setPromoMsg(`✅ Добавлено ${res.data.tokens_added} токенов!`);
      updateUser((prev) => ({ ...prev, remaining: prev.remaining + res.data.tokens_added }));
      setPromoCode('');
    } catch (err: any) {
      setPromoMsg(`❌ ${err.response?.data?.detail || 'Ошибка активации'}`);
    }
  };

  // ===== SHOP =====
  const loadShopConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/shop/config`);
      setShopConfig(res.data);
      setBuyTokens(res.data.min_tokens);
    } catch (err) { console.error(err); }
  };

  const openPaymentUrl = (url: string, label: string) => {
    localStorage.setItem('lc_pending_payment', label);
    setPendingLabel(label);
    if (window.self !== window.top) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  };

  const buyTokensAction = async () => {
    if (!user || shopLoading) return;
    setShopMsg('');
    setShopLoading(true);
    try {
      const res = await axios.post(`${API_URL}/shop/buy`, { user_id: user.user_id, tokens: buyTokens });
      openPaymentUrl(res.data.url, res.data.label);
    } catch (err: any) {
      setShopMsg(err.response?.data?.detail || 'Ошибка создания платежа');
    } finally {
      setShopLoading(false);
    }
  };

  const subscribeAction = async (plan: 'pro' | 'max') => {
    if (!user || shopLoading) return;
    setShopMsg('');
    setShopLoading(true);
    try {
      const res = await axios.post(`${API_URL}/shop/subscribe`, { user_id: user.user_id, plan });
      openPaymentUrl(res.data.url, res.data.label);
    } catch (err: any) {
      setShopMsg(err.response?.data?.detail || 'Ошибка создания платежа');
    } finally {
      setShopLoading(false);
    }
  };

  // Проверка статуса ожидающего платежа
  useEffect(() => {
    if (!pendingLabel || !user) return;
    let attempts = 0;
    const check = async () => {
      attempts++;
      try {
        const res = await axios.get(`${API_URL}/shop/status/${pendingLabel}`);
        if (res.data.status === 'paid') {
          localStorage.removeItem('lc_pending_payment');
          setPendingLabel(null);
          setPaymentSuccess('Успешно! Баланс пополнен');
          setTimeout(() => setPaymentSuccess(''), 4000);
          refreshUserData();
          return;
        }
        if (res.data.status === 'not_found') {
          // Платёж истёк (ссылка живёт 5 минут) или удалён — прекращаем ожидание
          localStorage.removeItem('lc_pending_payment');
          setPendingLabel(null);
          return;
        }
      } catch {}
      // Ссылка активна 5 минут: 60 попыток по 5 сек
      if (attempts < 60) setTimeout(check, 5000);
      else { localStorage.removeItem('lc_pending_payment'); setPendingLabel(null); }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLabel]);

  const loadAdminPayments = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/admin/payments`, { params: { admin_email: user.email, token: user.token } });
      setAdminPayments(res.data);
    } catch (err) { console.error(err); }
  };

  const loadAdminPrices = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/admin/shop-settings`, { params: { admin_email: user.email, token: user.token } });
      setAdminPrices(res.data);
    } catch (err) { console.error(err); }
  };

  const saveAdminPrices = async () => {
    if (!user) return;
    setPricesMsg('');
    try {
      await axios.post(`${API_URL}/admin/shop-settings`, { admin_email: user.email, token: user.token, settings: adminPrices });
      setPricesMsg('Сохранено');
      setTimeout(() => setPricesMsg(''), 2000);
    } catch (err: any) {
      setPricesMsg(err.response?.data?.detail || 'Ошибка');
    }
  };

  const togglePinChat = async (id: number) => {
    if (!user) return;
    try {
      await axios.post(`${API_URL}/chat/${id}/pin`, { user_id: user.user_id, token: user.token });
      loadChatHistory();
    } catch (err) { console.error(err); }
  };

  const loadAdminStats = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/admin/stats`, {
        params: { admin_email: user.email, token: user.token },
      });
      setAdminStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAdminLogs = async (errorsOnly: boolean) => {
    if (!user) return;
    try {
      const url = errorsOnly ? `${API_URL}/admin/logs/errors` : `${API_URL}/admin/logs`;
      const res = await axios.get(url, {
        params: { admin_email: user.email, token: user.token, limit: 100 },
      });
      setAdminLogs(res.data.logs || []);
      setShowErrorLogsOnly(errorsOnly);
    } catch (err) {
      console.error(err);
    }
  };

  // ===== Поддержка: пользователь =====
  const sendFeedback = async (msgIdx: number, rating: 'like' | 'dislike', aiText: string, comment?: string) => {
    if (!user || feedbackGiven[msgIdx]) return;
    try {
      await axios.post(`${API_URL}/feedback`, {
        user_id: user.user_id, token: user.token, chat_id: chatId,
        rating, comment: comment || '', ai_text: aiText,
      });
      setFeedbackGiven((prev) => ({ ...prev, [msgIdx]: rating }));
      setDislikeCommentFor(null);
      setDislikeCommentText('');
    } catch (err) {
      console.error(err);
    }
  };

  const loadAdminFeedback = async (filter: 'all' | 'like' | 'dislike' = 'all') => {
    if (!user) return;
    try {
      const params: any = { admin_email: user.email, token: user.token, limit: 150 };
      if (filter !== 'all') params.rating = filter;
      const res = await axios.get(`${API_URL}/admin/feedback`, { params });
      setAdminFeedback(res.data.feedback || []);
      setAdminFeedbackCounts({ likes: res.data.likes || 0, dislikes: res.data.dislikes || 0 });
      setAdminFeedbackFilter(filter);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteAdminFeedback = async (id: number) => {
    if (!user) return;
    try {
      await axios.delete(`${API_URL}/admin/feedback/${id}`, { params: { admin_email: user.email, token: user.token } });
      setAdminFeedback((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const loadSupportMessages = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/support/messages`, {
        params: { user_id: user.user_id, token: user.token },
      });
      setSupportMessages(res.data.messages || []);
      setSupportUnread(false);
    } catch (err) {
      console.error(err);
    }
  };

  const sendSupportMessage = async () => {
    if (!user || !supportInput.trim() || supportSending) return;
    setSupportSending(true);
    try {
      await axios.post(`${API_URL}/support/send`, {
        user_id: user.user_id, token: user.token, message: supportInput.trim(),
      });
      setSupportInput('');
      await loadSupportMessages();
    } catch (err) {
      console.error(err);
    } finally {
      setSupportSending(false);
    }
  };

  // ===== Поддержка: админ =====
  const loadAdminSupportChats = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/admin/support/chats`, {
        params: { admin_email: user.email, token: user.token },
      });
      setAdminSupportChats(res.data.chats || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openAdminSupportChat = async (chatId: number) => {
    if (!user) return;
    setAdminSupportMenuFor(null);
    try {
      const res = await axios.get(`${API_URL}/admin/support/chats/${chatId}/messages`, {
        params: { admin_email: user.email, token: user.token },
      });
      setAdminSupportDetail(res.data);
      setAdminSupportSelected(chatId);
    } catch (err) {
      console.error(err);
    }
  };

  const sendAdminSupportReply = async () => {
    if (!user || adminSupportSelected === null || !adminSupportReply.trim()) return;
    try {
      await axios.post(`${API_URL}/admin/support/reply`, {
        admin_email: user.email, token: user.token,
        chat_id: adminSupportSelected, message: adminSupportReply.trim(),
      });
      setAdminSupportReply('');
      await openAdminSupportChat(adminSupportSelected);
      await loadAdminSupportChats();
    } catch (err) {
      console.error(err);
    }
  };

  const blockSupportUser = async (chatId: number) => {
    if (!user) return;
    if (!window.confirm('Заблокировать этого пользователя? Он будет немедленно разлогинен и не сможет войти.')) return;
    try {
      await axios.post(`${API_URL}/admin/support/block`, { admin_email: user.email, token: user.token, chat_id: chatId });
      setAdminSupportMenuFor(null);
      await loadAdminSupportChats();
      if (adminSupportSelected === chatId) await openAdminSupportChat(chatId);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteSupportChat = async (chatId: number) => {
    if (!user) return;
    if (!window.confirm('Удалить этот чат поддержки безвозвратно?')) return;
    try {
      await axios.delete(`${API_URL}/admin/support/chats/${chatId}`, { params: { admin_email: user.email, token: user.token } });
      setAdminSupportMenuFor(null);
      if (adminSupportSelected === chatId) { setAdminSupportSelected(null); setAdminSupportDetail(null); }
      await loadAdminSupportChats();
    } catch (err) {
      console.error(err);
    }
  };

  const grantSupportTokens = async (chatId: number) => {
    if (!user) return;
    const amount = parseInt(adminGrantAmount, 10);
    if (!amount || amount <= 0) return;
    try {
      await axios.post(`${API_URL}/admin/support/grant-tokens`, { admin_email: user.email, token: user.token, chat_id: chatId, amount });
      setAdminSupportMenuFor(null);
      alert(`Начислено ${amount.toLocaleString('ru-RU')} 🔑`);
      if (adminSupportSelected === chatId) await openAdminSupportChat(chatId);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSupportLongPressStart = (chatId: number) => {
    supportPressTimer.current = setTimeout(() => setAdminSupportMenuFor(chatId), 500);
  };
  const handleSupportLongPressEnd = () => {
    if (supportPressTimer.current) clearTimeout(supportPressTimer.current);
  };

  const generateKey = async () => {
    if (!user) return;
    try {
      const res = await axios.post(`${API_URL}/admin/generate-key`, {
        admin_email: user.email,
        token: user.token,
        tokens_amount: parseInt(keyTokens),
        max_activations: parseInt(keyActivations),
        expires_days: keyExpireDays ? parseInt(keyExpireDays) : null,
      });
      setGeneratedKey(res.data.key_code);
      loadAdminStats();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка генерации');
    }
  };

  const deleteKey = async (keyCode: string) => {
    if (!user) return;
    if (!window.confirm(`Удалить ключ ${keyCode}?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/keys/${keyCode}`, {
        params: { admin_email: user.email, token: user.token },
      });
      loadAdminStats();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  // ===== Админка ключей OpenRouter =====
  const loadV0Keys = async (providerOverride?: 'openrouter' | 'glm' | 'groq') => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/admin/openrouter-keys`, { params: { admin_email: user.email, token: user.token, provider: providerOverride || keyProvider } });
      setV0Keys(res.data.keys || []);
    } catch (err) { console.error(err); }
  };

  const addV0Key = async () => {
    if (!user || !newV0Key.trim()) return;
    setV0KeysMsg('');
    try {
      await axios.post(`${API_URL}/admin/openrouter-keys`, {
        admin_email: user.email,
        token: user.token,
        key: newV0Key.trim(),
        label: newV0Label.trim(),
        provider: keyProvider,
      });
      setNewV0Key('');
      setNewV0Label('');
      setV0KeysMsg('Ключ добавлен');
      setTimeout(() => setV0KeysMsg(''), 2000);
      loadV0Keys();
    } catch (err: any) {
      setV0KeysMsg(err.response?.data?.detail || 'Ошибка добавления');
    }
  };

  const deleteV0Key = async (id: number) => {
    if (!user) return;
    if (!window.confirm('Удалить этот ключ OpenRouter?')) return;
    try {
      await axios.delete(`${API_URL}/admin/openrouter-keys/${id}`, { params: { admin_email: user.email, token: user.token } });
      loadV0Keys();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const toggleV0Key = async (id: number) => {
    if (!user) return;
    try {
      await axios.post(`${API_URL}/admin/openrouter-keys/${id}/toggle`, { admin_email: user.email, token: user.token });
      loadV0Keys();
    } catch (err) { console.error(err); }
  };

  const checkV0Keys = async () => {
    if (!user || v0KeysChecking) return;
    setV0KeysChecking(true);
    setV0KeysMsg('');
    try {
      await axios.post(`${API_URL}/admin/openrouter-keys/check`, { admin_email: user.email, token: user.token, provider: keyProvider });
      setV0KeysMsg('Проверка завершена');
      setTimeout(() => setV0KeysMsg(''), 2000);
      loadV0Keys();
    } catch (err: any) {
      setV0KeysMsg(err.response?.data?.detail || 'Ошибка проверки');
    } finally {
      setV0KeysChecking(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('lc_user');
    setUser(null);
    setMessages([]);
    setShowSettings(false);
    setShowAdmin(false);
    setEmail('');
    setPassword('');
    setAuthMode('login');
  };

  useEffect(() => {
    // Автопрокрутка вниз только если пользователь не отскроллил вверх сам.
    // Мгновенно (без 'smooth'), чтобы частые обновления стрима не создавали
    // накладывающиеся анимации, которые перебивают ручной скролл пользователя.
    if (stickToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [messages]);

  useEffect(() => {
    if (showAdmin) {
      loadAdminStats();
      loadAdminLogs(false);
      const iv = setInterval(loadAdminStats, 5000);
      return () => clearInterval(iv);
    }
  }, [showAdmin]);

  useEffect(() => {
    if (sidebarOpen) loadChatHistory();
  }, [sidebarOpen]);

  useEffect(() => {
    // "Чёрный экран" после деплоя чаще всего - попытка догрузить старый несуществующий чанк
    // (index.html уже новый, а открытая вкладка ещё держит старые ссылки на JS-чанки).
    // Ловим это и показываем тот же полноэкранный "доступно обновление" вместо пустого экрана.
    const chunkFailRe = /loading chunk|failed to fetch dynamically imported module|unexpected token '<'/i;
    const onError = (e: ErrorEvent) => {
      if (chunkFailRe.test(String(e?.message || ''))) setUpdateAvailable(true);
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const msg = String((e?.reason && e.reason.message) || e?.reason || '');
      if (chunkFailRe.test(msg)) setUpdateAvailable(true);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  if (updateAvailable) {
    return (
      <div className="update-overlay">
        <div className="update-card">
          {updateStage === 'prompt' && (
            <>
              <div className="update-spin" />
              <h2>Доступно обновление</h2>
              <p className="update-sub">Займёт меньше минуты{updateSize ? ` · ${updateSize}` : ''}</p>
              {updateNotes.length > 0 && (
                <ul className="update-notes">
                  {updateNotes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              )}
              <button className="auth-btn" onClick={startUpdateInstall}>Обновить</button>
            </>
          )}
          {updateStage === 'installing' && (
            <>
              <div className="update-spin" />
              <h2>Устанавливаем обновление</h2>
              <p className="update-sub">{updateProgress}%</p>
              <div className="update-progress-track">
                <div className="update-progress-fill" style={{ width: `${updateProgress}%` }} />
              </div>
            </>
          )}
          {updateStage === 'done' && (
            <>
              <div className="update-done-check">✓</div>
              <h2>Готово!</h2>
              <p className="update-sub">Обновление установлено</p>
              <button className="auth-btn" onClick={applyUpdate}>ОК</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ===== AUTH SCREENS =====
  if (!user) {
    return (
      <div className="login-container">
        <div className="login-content">
          <img src={starIconSrc} alt="Legends Code" className="login-logo" />
          <h1>Legends Code</h1>

          {authMode === 'login' && (
            <>
              <p>{t.loginTitle}</p>
              <input className="auth-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input className="auth-input" type="password" placeholder={t.password} value={password} onChange={(e) => setPassword(e.target.value)} />
              {authError && <div className="auth-error">{authError}</div>}
              <button className="auth-btn" onClick={handleLogin} disabled={authLoading}>
                {authLoading ? <><span className="auth-spinner" />{t.loggingIn}</> : t.login}
              </button>
              {Capacitor.isNativePlatform() && (
                <button
                  className="auth-btn google-glass-btn"
                  style={{marginTop: 8}}
                  onClick={handleGoogleLogin}
                  disabled={authLoading}
                >
                  {authLoading ? <span className="auth-spinner" /> : (
                    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.7 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l6-6C34.5 5.9 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.6 18.9 13 24 13c3.1 0 5.8 1.1 8 3l6-6C34.5 5.9 29.6 4 24 4c-7.5 0-14 4.2-17.7 10.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.3-1.8 14-4.9l-6.4-5.3C29.6 35.4 26.9 36 24 36c-5.4 0-9.9-3.3-11.3-8l-6.5 5C9.9 39.6 16.4 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3-3.5 5.5-6.6 6.9l6.4 5.3C38.4 37 44 31 44 24c0-1.3-.1-2.7-.4-3.5z"/></svg>
                  )}
                  Войти через Google
                </button>
              )}
              <div className="auth-switch" onClick={() => { setAuthMode('register'); setAuthError(''); }}>
                Нет аккаунта? Зарегистрироваться
              </div>
            </>
          )}

          {authMode === 'register' && (
            <>
              <p>{t.registerTitle}</p>
              <input className="auth-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input className="auth-input" type="password" placeholder={t.password} value={password} onChange={(e) => setPassword(e.target.value)} />
              <div id="recaptcha-container" style={{ margin: '8px 0', display: 'flex', justifyContent: 'center' }}></div>
              <label className="terms-checkbox" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '10px 0', fontSize: '12.5px', color: '#9a9690', cursor: 'pointer', textAlign: 'left' }}>
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  style={{ marginTop: '2px', flexShrink: 0 }}
                />
                <span>
                  Я принимаю{' '}
                  <a href="/api/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#D97757' }}>
                    политику конфиденциальности
                  </a>
                  {' '}и{' '}
                  <a href="/api/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#D97757' }}>
                    пользовательское соглашение
                  </a>
                </span>
              </label>
              {authError && <div className="auth-error">{authError}</div>}
              <button className="auth-btn" onClick={handleRegister} disabled={authLoading || !captchaToken || !agreeTerms}>
                {authLoading ? <><span className="auth-spinner" />{t.sending}</> : t.register}
              </button>
              <div className="auth-switch" onClick={() => { setAuthMode('login'); setAuthError(''); }}>
                Уже есть аккаунт? Войти
              </div>
            </>
          )}

          {authMode === 'verify' && (
            <>
              <p>{t.enterCode}</p>
              <input className="auth-input" type="text" placeholder={t.codePlaceholder} value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} maxLength={6} />
              <p className="code-hint">{t.codeHint}</p>
              {authError && <div className="auth-error">{authError}</div>}
              <button className="auth-btn" onClick={handleVerify} disabled={authLoading}>
                {authLoading ? <><span className="auth-spinner" />{t.checking}</> : t.verify}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const copyRefLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = link; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    setRefCopied(true);
    setTimeout(() => setRefCopied(false), 1600);
  };

  const isAdmin = user.email === ADMIN_EMAIL;

  return (
    <div
      className="main-container"
      style={{ background: bgColor, '--bg-color': bgColor } as React.CSSProperties}
      onContextMenu={(e) => {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') e.preventDefault();
      }}
    >
      {showSplash && (
        <div className="splash-overlay">
          <div className="splash-star-wrap">
            <img src="/assets/legends-logo.png" alt="" className="splash-star-img" />
          </div>
        </div>
      )}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <span className="sidebar-brand-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97757" strokeWidth="2.4" strokeLinecap="round"><path d="M12 3a9 9 0 1 0 9 9" /></svg>
              </span>
              <span className="sidebar-title">Legends Code</span>
            </div>
            <button className="close-sidebar" onClick={() => setSidebarOpen(false)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
          <button className="new-chat-sidebar-btn" onClick={startNewChat}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            {t.newChat}
          </button>
          {chatList.length > 0 && <div className="sidebar-section-label">{uiLang === 'ru' ? 'Чаты' : 'Chats'}</div>}
          <div className="chat-history">
            {chatList.length === 0 ? (
              <div className="no-chats">{t.noChats}</div>
            ) : (
              chatList.map((c) => (
                <div key={c.id} className={`chat-history-item ${c.pinned ? 'pinned' : ''}`} onClick={() => openChat(c.id)}>
                  {c.pinned && (
                    <span className="pin-indicator">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#D97757" stroke="#D97757" strokeWidth="1"><path d="M12 17v5"></path><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"></path></svg>
                    </span>
                  )}
                  <span className="chat-history-title">{c.title}</span>
                  {deleteConfirmId === c.id ? (
                    <span className="chat-delete-confirm">
                      <button onClick={(e) => { e.stopPropagation(); confirmDeleteChat(c.id); }}>✓</button>
                      <button onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(null); }}>✕</button>
                    </span>
                  ) : (
                    <span className="chat-item-actions">
                      <button
                        className="chat-pin-btn"
                        title={c.pinned ? 'Открепить' : 'Закрепить'}
                        onClick={(e) => { e.stopPropagation(); togglePinChat(c.id); }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={c.pinned ? '#D97757' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 17v5"></path>
                          <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"></path>
                        </svg>
                      </button>
                      <button className="chat-delete-btn" onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(c.id); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                      </button>
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {sidebarOpen && <div className="overlay" onClick={() => setSidebarOpen(false)}></div>}

      <div className="chat-main">
        <div className="chat-header">
          <button className="menu-btn" onClick={() => { vibrateShort(); setSidebarOpen(true); }}>☰</button>
          <span className="chat-header-title">{t.newChat}</span>
          <button className="profile-btn" onClick={() => { if (!showSettings) { refreshUserData(); vibrateShort(); } setShowSettings(!showSettings); }}>⋯</button>
        </div>

          <div className="chat-content" ref={chatContentRef} onWheel={wheelHandler} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onScroll={onChatScroll}>
          {messages.length === 0 ? (
            <div className="welcome-section">
              <div className="welcome-star">
                <img
                  src={starVideoSrc}
                  alt="Legends Code"
                  className="welcome-logo-img"
                />
              </div>
              <h1 className="brand-title">{welcomeTyped}{welcomeTyped.length < welcomeFull.length && <span className="type-caret">|</span>}</h1>
            </div>
          ) : (
            <div className="messages">
              {messages.map((msg, idx) => {
                const isLastAssistant = idx === messages.length - 1 && msg.role === 'assistant';
                const isEmpty = msg.content === '';
                const fileMatch = msg.role === 'user' ? msg.content.match(/\[Файл: (.+?)\]\n```\n[\s\S]*?\n```/) : null;
                if (fileMatch) {
                  msg = { ...msg, content: msg.content.replace(fileMatch[0], '').trim() };
                }
                return (
                  <div key={idx} className={`message ${msg.role}`}>
                    {fileMatch && (
                      <div className="msg-file-chip">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                        <span>{fileMatch[1]}</span>
                      </div>
                    )}
                    <div
                      className={`message-content${isLastAssistant && isEmpty && loading ? ' message-content-loading' : ''}`}
                      onContextMenu={(e) => e.preventDefault()}
                      onMouseDown={() => msg.content && (longPressTimer.current = setTimeout(() => {
                        if (navigator.clipboard && window.isSecureContext) {
                          navigator.clipboard.writeText(stripThinkFront(msg.content));
                        } else {
                          const textarea = document.createElement('textarea');
                          textarea.value = stripThinkFront(msg.content);
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.focus();
                          textarea.select();
                          try { document.execCommand('copy'); } catch (e) {}
                          document.body.removeChild(textarea);
                        }
                        setCopiedIdx(idx);
                        setTimeout(() => setCopiedIdx(null), 1200);
                      }, 500))}
                      onMouseUp={() => longPressTimer.current && clearTimeout(longPressTimer.current)}
                      onMouseLeave={() => longPressTimer.current && clearTimeout(longPressTimer.current)}
                      onTouchStart={() => msg.content && (longPressTimer.current = setTimeout(() => {
                        if (navigator.clipboard && window.isSecureContext) {
                          navigator.clipboard.writeText(stripThinkFront(msg.content));
                        } else {
                          const textarea = document.createElement('textarea');
                          textarea.value = stripThinkFront(msg.content);
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.focus();
                          textarea.select();
                          try { document.execCommand('copy'); } catch (e) {}
                          document.body.removeChild(textarea);
                        }
                        setCopiedIdx(idx);
                        setTimeout(() => setCopiedIdx(null), 1200);
                      }, 500))}
                      onTouchEnd={() => longPressTimer.current && clearTimeout(longPressTimer.current)}
                    >
                      {msg.content.startsWith('__REFUSAL__') ? (
                        <span className="refusal-block">⚠️ {msg.content.replace('__REFUSAL__', '')}</span>
                      ) : isLastAssistant && isEmpty && loading && genStarted ? (
                        <span className="thinking-row">
                          <span className="thinking-star"><img src={starIconSrc} alt="" className="loading-star-img" style={{ width: 16, height: 16 }} /></span>
                          <span className="thinking-phrase">{thinkingPhrase}</span>
                        </span>
                      ) : isLastAssistant && isEmpty && loading && !genStarted ? (
                        <span className="pre-gen-loading">
                          <img src={starIconSrc} alt="" className="loading-star-img" style={{ width: 22, height: 22 }} />
                        </span>
                      ) : msg.role === 'assistant' ? renderAssistantContent(msg.content, isLastAssistant && loading) : msg.content}
                      {copiedIdx === idx && <span className="copied-hint">{t.copied}</span>}
                    </div>
                    {msg.role === 'assistant' && msg.content && !(isLastAssistant && loading) && !msg.content.startsWith('__REFUSAL__') && (
                      <div className="msg-logo-row">
                        <span className="msg-logo"><StarIcon size={14} /></span>
                        {typeof msg.tokens === 'number' && msg.tokens > 0 && (
                          <span className="msg-tokens">{msg.tokens.toLocaleString('ru-RU')} {pluralTokens(msg.tokens)}</span>
                        )}
                        <div className="msg-feedback">
                          {feedbackGiven[idx] ? (
                            <span className="feedback-thanks">{feedbackGiven[idx] === 'like' ? '👍' : '👎'} Спасибо за отзыв</span>
                          ) : (
                            <>
                              <button className="feedback-btn" onClick={() => sendFeedback(idx, 'like', msg.content)} title="Полезно">👍</button>
                              <button className="feedback-btn" onClick={() => setDislikeCommentFor(dislikeCommentFor === idx ? null : idx)} title="Не полезно">👎</button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {dislikeCommentFor === idx && (
                      <div className="dislike-comment-box">
                        <input
                          className="auth-input"
                          type="text"
                          placeholder="Что не так? (необязательно)"
                          value={dislikeCommentText}
                          onChange={(e) => setDislikeCommentText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') sendFeedback(idx, 'dislike', msg.content, dislikeCommentText); }}
                        />
                        <div className="dislike-comment-btns">
                          <button className="auth-btn dislike-send-btn" onClick={() => sendFeedback(idx, 'dislike', msg.content, dislikeCommentText)}>Отправить</button>
                          <button className="dislike-skip-btn" onClick={() => sendFeedback(idx, 'dislike', msg.content)}>Без комментария</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {showScrollDown && (
          <button
            className="scroll-down-btn"
            onClick={() => {
              stickToBottomRef.current = true;
              setShowScrollDown(false);
              messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
            }}
            aria-label={uiLang === 'ru' ? 'Вниз чата' : 'Scroll to bottom'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
          </button>
        )}

        <div className="chat-footer">
          {chatBlocked ? (
            <div className="chat-blocked-banner">
              <span className="chat-blocked-icon">🚫</span>
              <p>Чат заблокирован за нарушение правил общения (нецензурная лексика). Начните новый чат, чтобы продолжить.</p>
              <button className="chat-blocked-new-btn" onClick={startNewChat}>Новый чат</button>
            </div>
          ) : (
          <div className="composer">
            {attachedFile && (
              <div className="attach-chip">
                <span className="attach-chip-name">{attachedFile.name}</span>
                <button className="attach-chip-x" onClick={() => setAttachedFile(null)}>✕</button>
              </div>
            )}
            {attachedImages.length > 0 && (
              <div className="attach-images-row">
                {attachedImages.map((img, i) => (
                  <div key={i} className="attach-image-thumb">
                    <img src={img} alt="" />
                    <button className="attach-image-x" onClick={() => setAttachedImages((prev) => prev.filter((_, idx) => idx !== i))}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <textarea
              ref={composerInputRef}
              className="composer-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={t.inputPlaceholder}
              disabled={loading}
              rows={1}
            />
            <div className="composer-row">
              <button className="plus-btn" onClick={() => fileInputRef.current && fileInputRef.current.click()} title={t.attach}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              </button>
              <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFilePick} />
              {PHOTO_UPLOAD_ENABLED && (
                <>
                  <button className="plus-btn" onClick={() => imageInputRef.current && imageInputRef.current.click()} title={uiLang === 'ru' ? 'Прикрепить фото (до 5)' : 'Attach photos (up to 5)'}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
                  </button>
                  <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImagePick} />
                </>
              )}
              {(
                <button
                  className={`web-search-btn ${webSearchEnabled ? 'active' : ''}`}
                  onClick={() => setWebSearchEnabled((v) => !v)}
                  title={uiLang === 'ru' ? 'Поиск в интернете' : 'Web search'}
                  aria-pressed={webSearchEnabled}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                </button>
              )}
              {voiceSupported && (
                <button
                  className={`web-search-btn mic-btn ${isRecording ? 'recording' : ''}`}
                  onClick={toggleVoiceInput}
                  title={uiLang === 'ru' ? 'Голосовой ввод' : 'Voice input'}
                  aria-pressed={isRecording}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
                </button>
              )}
              <button
                className="model-btn"
                onClick={() => setShowModelPicker(true)}
              >
                {selectedModel === 'main' ? 'Legends Code' : selectedModel === 'v2' ? 'LC V2 Code' : 'Legends Code Mini'}
              </button>
              <div className="composer-spacer"></div>
              {loading ? (
                <button className="send-btn stop-btn" onClick={stopGeneration} aria-label={uiLang === 'ru' ? 'Остановить генерацию' : 'Stop generation'}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <rect x="7" y="7" width="10" height="10" rx="1.5"></rect>
                  </svg>
                </button>
              ) : (
                <button className="send-btn" onClick={() => sendMessage()} disabled={!input.trim()}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="19" x2="12" y2="5"></line>
                    <polyline points="5 12 12 5 19 12"></polyline>
                  </svg>
                </button>
              )}
            </div>
          </div>
          )}
        </div>
      </div>

      {showModelPicker && (
        <div className="model-picker-overlay" onClick={() => setShowModelPicker(false)}>
          <div className="model-picker-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="model-picker-handle"></div>
            <div
              className={`model-option ${selectedModel === 'mini' ? 'active' : ''}`}
              onClick={() => { setSelectedModel('mini'); setShowModelPicker(false); }}
            >
              <div className="model-option-main">
                <div className="model-option-title">Legends Code Mini</div>
                <div className="model-option-desc">{t.modelDesc1}</div>
              </div>
              {selectedModel === 'mini' && <span className="model-check">✓</span>}
            </div>
            <div
              className={`model-option ${selectedModel === 'main' ? 'active' : ''}`}
              onClick={() => { setSelectedModel('main'); setShowModelPicker(false); }}
            >
              <div className="model-option-main">
                <div className="model-option-title">LC Model</div>
                <div className="model-option-desc">{t.modelDesc2}</div>
              </div>
              {selectedModel === 'main' && <span className="model-check">✓</span>}
            </div>
            <div
              className={`model-option ${selectedModel === 'v2' ? 'active' : ''}`}
              onClick={() => { setSelectedModel('v2'); setShowModelPicker(false); }}
            >
              <div className="model-option-main">
                <div className="model-option-title">Legends Code V2 Code <span className="model-badge-new">NEW</span></div>
                <div className="model-option-desc">{t.modelDesc3}</div>
              </div>
              {selectedModel === 'v2' && <span className="model-check">✓</span>}
            </div>
            <div className="model-picker-divider" />
            <div
              className={`thinking-toggle-row ${selectedModel !== 'v2' ? 'disabled' : ''}`}
              onClick={() => {
                if (selectedModel !== 'v2') return;
                const next = !thinkingEnabled;
                setThinkingEnabled(next);
                localStorage.setItem('lc_thinking', next ? '1' : '0');
              }}
            >
              <div className="thinking-toggle-text">
                <span>🧠 {uiLang === 'ru' ? 'Режим размышлений' : 'Thinking mode'}</span>
                <span className="thinking-toggle-sub">
                  {selectedModel === 'v2'
                    ? (uiLang === 'ru' ? 'Дольше думает, отвечает точнее' : 'Thinks longer, answers better')
                    : (uiLang === 'ru' ? 'Доступно только для LC V2 Code' : 'Available only for LC V2 Code')}
                </span>
              </div>
              <span className={`toggle-pill ${thinkingEnabled && selectedModel === 'v2' ? 'on' : ''}`}><span className="toggle-dot" /></span>
            </div>
          </div>
        </div>
      )}
      {showSettings && (
        <div className="settings-panel">
          <div className="settings-header">
            <h2>{t.settings} <span className="app-version">v{APP_VERSION}</span></h2>
            <button onClick={() => setShowSettings(false)}>✕</button>
          </div>

          <div className="settings-content">
            <div className="user-card">
              <div className="user-card-top">
                <span className="user-email">{user.email}</span>
                <button
                  className="lang-toggle-btn"
                  onClick={() => { const next = uiLang === 'ru' ? 'en' : 'ru'; setUiLang(next); localStorage.setItem('uiLang', next); }}
                  title={uiLang === 'ru' ? 'Язык' : 'Language'}
                >
                  {uiLang === 'ru' ? 'RU' : 'EN'}
                </button>
              </div>
              <div className="user-card-bottom">
                <span className="glass-badge">{user.remaining} 🔑</span>
                <button
                  className="limits-clock-btn"
                  onClick={() => { setShowLimits(true); setShowSettings(false); refreshUserData(); }}
                  title={uiLang === 'ru' ? 'Лимиты' : 'Limits'}
                >
                  ⏱️
                </button>
              </div>
            </div>
            {user.sub_plan && (
              <div className="sub-badge-card">
                {user.sub_plan === 'pro' ? 'Подписка PRO' : 'Подписка MAX'}
                {user.sub_expires && ` · до ${new Date(user.sub_expires).toLocaleDateString()}`}
              </div>
            )}
            <div className="settings-item shop-item" onClick={() => { setShowShop(true); setShowSettings(false); loadShopConfig(); }}>
              🛒  {uiLang === 'ru' ? 'Магазин' : 'Shop'}
            </div>
            <div className="settings-item" onClick={() => { setShowSupportChat(true); setShowSettings(false); loadSupportMessages(); }}>
              💬  {uiLang === 'ru' ? 'Поддержка' : 'Support'}
            </div>
            <div className="theme-picker">
              <p className="promo-label">{uiLang === 'ru' ? 'Цвет фона' : 'Background color'}</p>
              <div className="theme-swatches">
                {[
                  { c: '#1a1a1a', name: 'Уголь' },
                  { c: '#14202b', name: 'Океан' },
                  { c: '#1f1a24', name: 'Слива' },
                  { c: '#101512', name: 'Лес' },
                  { c: '#241a1a', name: 'Уголёк' },
                  { c: '#1a1f24', name: 'Сланец' },
                  { c: '#221c14', name: 'Кофе' },
                  { c: '#181822', name: 'Полночь' },
                ].map(({ c, name }) => (
                  <div key={c} className="theme-swatch-wrap" title={name}>
                    <div
                      className={`theme-swatch ${bgColor === c ? 'active' : ''}`}
                      style={{ background: c }}
                      onClick={() => applyBgColor(c)}
                    >
                      {bgColor === c && (
                        <svg className="theme-swatch-check" width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke="#D97757" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="promo-section">
              <p className="promo-label">{t.promo}</p>
              <input
                className="auth-input"
                type="text"
                placeholder="sk_ant03-..."
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
              <button className="auth-btn" onClick={redeemPromo}>{t.activate}</button>
              {promoMsg && <div className="promo-msg">{promoMsg}</div>}
            </div>

            <div className="grow-card">
              <div className="grow-card-head">
                <span className="grow-card-title">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                  {uiLang === 'ru' ? 'Приглашай друзей' : 'Invite friends'}
                </span>
                <span className="grow-card-badge">{user.referrals_this_week ?? 0}/{user.referral_weekly_limit ?? 10}</span>
              </div>
              <p className="grow-card-desc">
                {uiLang === 'ru'
                  ? `+3000 🔑 тебе и другу за каждого, кто зайдёт по ссылке`
                  : `+3000 🔑 for you and your friend for every signup`}
              </p>
              {user.referral_code && (
                <div className="ref-link-row">
                  <span className="ref-link-text">{`claude-ai.duckdns.org?ref=${user.referral_code}`}</span>
                  <button
                    className="ref-copy-btn"
                    onClick={() => copyRefLink(`https://claude-ai.duckdns.org?ref=${user.referral_code}`)}
                  >
                    {refCopied ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                    )}
                  </button>
                </div>
              )}
              {(user.referrals_total ?? 0) > 0 && (
                <p className="grow-card-total">
                  {uiLang === 'ru' ? `Всего приглашено: ${user.referrals_total}` : `Total invited: ${user.referrals_total}`}
                </p>
              )}
              {isAdmin && (
                <div className="admin-entry-row" onClick={() => { setShowAdmin(true); setShowSettings(false); }}>
                  <span>⚡ {t.adminPanel}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </div>
              )}
            </div>

            <div className="settings-item delete-all-item" onClick={deleteAllChats}>
              🗑  {deleteAllConfirm
                ? (uiLang === 'ru' ? 'Точно удалить все чаты? Нажми ещё раз' : 'Delete ALL chats? Tap again')
                : (uiLang === 'ru' ? 'Удалить все чаты' : 'Delete all chats')}
            </div>

            <button className="logout-btn" onClick={logout}>Log out</button>
          </div>
        </div>
      )}

      {showShop && (
        <div className="shop-overlay" onClick={() => setShowShop(false)}>
          <div className="shop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="shop-aurora shop-aurora-1"></div>
            <div className="shop-aurora shop-aurora-2"></div>
            <div className="shop-modal-inner">
              <div className="settings-header shop-header">
                <h2 className="shop-title-gradient">{uiLang === 'ru' ? 'Магазин' : 'Shop'}</h2>
                <button onClick={() => setShowShop(false)}>✕</button>
              </div>
              <div className="shop-content">
                {paymentSuccess && <div className="shop-success">✅ {paymentSuccess}</div>}
                {pendingLabel && !paymentSuccess && (
                  <div className="shop-pending">⏳ Ожидаем подтверждение оплаты (до 5 минут)...</div>
                )}

                <div className="shop-section shop-card-glass">
                  <div className="shop-section-title">
                    <span className="shop-icon-badge">🔑</span>
                    Токены
                  </div>
                  <div className="shop-price-hint">
                    {shopConfig ? `10 000 токенов — ${shopConfig.price_per_10k} ₽` : '...'}
                  </div>
                  <div className="token-slider-row">
                    <input
                      type="range"
                      min={shopConfig?.min_tokens || 30000}
                      max={shopConfig?.max_tokens || 1000000}
                      step={10000}
                      value={buyTokens}
                      onChange={(e) => setBuyTokens(parseInt(e.target.value))}
                      className="token-slider"
                    />
                  </div>
                  <div className="token-amount-row">
                    <span className="token-amount">{buyTokens.toLocaleString('ru-RU')} токенов</span>
                    <span className="token-price">
                      {shopConfig ? `${((buyTokens / 10000) * shopConfig.price_per_10k).toFixed(0)} ₽` : ''}
                    </span>
                  </div>
                  <div className="shop-note">Купленные токены не сгорают при обновлении лимита</div>
                  <div className="shop-warn">Ссылка на оплату активна только 5 минут</div>
                  <button className="shop-buy-btn" onClick={buyTokensAction} disabled={shopLoading}>
                    {shopLoading ? 'Создание ссылки...' : 'Подтвердить покупку'}
                  </button>
                </div>

                {shopConfig && (
                  <div className="shop-section">
                    <div className="shop-section-title">
                      <span className="shop-icon-badge">⚡</span>
                      Подписки
                    </div>
                    {(['pro', 'max'] as const).map((plan) => {
                      const sub = shopConfig.subscriptions[plan];
                      if (!sub) return null;
                      const isMax = plan === 'max';
                      return (
                        <div key={plan} className={`sub-card ${plan}`}>
                          {isMax && <div className="sub-card-badge">★ Популярный выбор</div>}
                          <div className="sub-card-head">
                            <span className="sub-card-title">{sub.title.replace('Подписка ', '')}</span>
                            <span className="sub-card-price-row">
                              <span className="sub-card-price">{sub.price} ₽</span>
                              <span className="sub-card-price-period">/мес</span>
                            </span>
                          </div>
                          <div className="sub-card-limit">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            {sub.limit.toLocaleString('ru-RU')} токенов / 4 часа
                          </div>
                          <div className="sub-card-desc">{sub.description}</div>
                          <button
                            className={`shop-buy-btn sub-buy ${isMax ? 'sub-buy-max' : ''}`}
                            onClick={() => subscribeAction(plan)}
                            disabled={shopLoading || user.sub_plan === plan}
                          >
                            {user.sub_plan === plan ? '✓ Активна' : 'Оформить'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {shopMsg && <div className="shop-error">{shopMsg}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {showLimits && (
        <div className="shop-overlay" onClick={() => setShowLimits(false)}>
          <div className="shop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="shop-aurora shop-aurora-1"></div>
            <div className="shop-aurora shop-aurora-2"></div>
            <div className="shop-modal-inner">
              <div className="settings-header shop-header">
                <h2 className="shop-title-gradient">{uiLang === 'ru' ? 'Лимиты' : 'Limits'}</h2>
                <button onClick={() => setShowLimits(false)}>✕</button>
              </div>
              <div className="shop-content">
                <div className="shop-section shop-card-glass">
                  <div className="shop-section-title">
                    <span className="shop-icon-badge">⏱️</span>
                    {uiLang === 'ru' ? 'Токены (4 часа)' : 'Tokens (4 hours)'}
                  </div>
                  <div className="token-amount-row">
                    <span className="token-amount">
                      {Math.max(0, (user.tokens_limit || 0) - (user.tokens_used || 0)).toLocaleString('ru-RU')} / {(user.tokens_limit || 0).toLocaleString('ru-RU')}
                    </span>
                  </div>
                  <div className="shop-note">
                    {uiLang === 'ru'
                      ? 'Сбрасывается каждые 4 часа автоматически.'
                      : 'Resets automatically every 4 hours.'}
                  </div>
                </div>

                {user.week_resets_left !== null && user.week_resets_left !== undefined && (
                  <div className="shop-section shop-card-glass">
                    <div className="shop-section-title">
                      <span className="shop-icon-badge">📅</span>
                      {uiLang === 'ru' ? 'Обновления в неделю' : 'Weekly refreshes'}
                    </div>
                    <div className="token-amount-row">
                      <span className="token-amount">
                        {user.week_resets_left} / 6 {uiLang === 'ru' ? 'осталось' : 'left'}
                      </span>
                    </div>
                    <div className="shop-note" style={{ color: user.week_resets_left === 0 ? '#ff6b6b' : undefined }}>
                      {uiLang === 'ru'
                        ? 'На бесплатном тарифе — не более 6 полных обновлений 4-часового лимита в неделю. У подписки PRO/MAX такого потолка нет.'
                        : 'On the free plan — up to 6 full 4-hour limit refreshes per week. PRO/MAX subscriptions have no such cap.'}
                    </div>
                    {user.week_reset_at && (
                      <div className="shop-note">
                        {uiLang === 'ru' ? 'Неделя обновится: ' : 'Week resets: '}
                        {new Date(user.week_reset_at).toLocaleString('ru-RU')}
                      </div>
                    )}
                  </div>
                )}

                {!user.sub_plan && (
                  <button className="shop-buy-btn" onClick={() => { setShowLimits(false); setShowShop(true); loadShopConfig(); }}>
                    {uiLang === 'ru' ? 'Оформить подписку без потолка' : 'Get unlimited subscription'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showSupportChat && (
        <div className="shop-overlay" onClick={() => setShowSupportChat(false)}>
          <div className="shop-modal support-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-header shop-header">
              <h2 className="shop-title-gradient">{uiLang === 'ru' ? 'Поддержка' : 'Support'}</h2>
              <button onClick={() => setShowSupportChat(false)}>✕</button>
            </div>
            <div className="support-messages">
              {supportMessages.length === 0 && (
                <p className="support-empty">
                  {uiLang === 'ru'
                    ? 'Опиши проблему или вопрос — ответит администратор лично, обычно в течение дня.'
                    : 'Describe your issue — the admin will reply personally, usually within a day.'}
                </p>
              )}
              {supportMessages.map((m, i) => (
                <div key={i} className={`support-msg ${m.sender === 'admin' ? 'from-admin' : 'from-user'}`}>
                  <div className="support-msg-bubble">{m.message}</div>
                  <div className="support-msg-time">{new Date(m.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              ))}
            </div>
            <div className="support-input-row">
              <input
                className="auth-input"
                type="text"
                placeholder={uiLang === 'ru' ? 'Сообщение...' : 'Message...'}
                value={supportInput}
                onChange={(e) => setSupportInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendSupportMessage(); }}
              />
              <button className="auth-btn support-send-btn" onClick={sendSupportMessage} disabled={supportSending || !supportInput.trim()}>
                {uiLang === 'ru' ? 'Отправить' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {outOfTokens && (
        <div className="shop-overlay" onClick={() => setOutOfTokens(false)}>
          <div className="shop-modal out-of-tokens" onClick={(e) => e.stopPropagation()}>
            <div className="oot-icon">🔑</div>
            <h3>У вас закончились токены</h3>
            <p>Хотите ли вы приобрести ещё? Купленные токены не сгорают, или дождитесь бесплатного обновления лимита.</p>
            <button className="shop-buy-btn" onClick={() => { setOutOfTokens(false); setShowShop(true); loadShopConfig(); }}>
              Открыть магазин
            </button>
            <button className="oot-later" onClick={() => setOutOfTokens(false)}>Позже</button>
          </div>
        </div>
      )}

      {showAdmin && isAdmin && (
        <div className="settings-panel admin-panel">
          <div className="settings-header">
            <h2>{t.adminPanel}</h2>
            <button onClick={() => setShowAdmin(false)}>✕</button>
          </div>

          <div className="admin-tabs">
            {([
              ['stats', 'Статистика'],
              ['support', `Поддержка${adminSupportChats.some(c => c.unread_by_admin) ? ' 🔴' : ''}`],
              ['feedback', 'Отзывы'],
              ['keys', 'Ключи'],
              ['openrouter', 'OpenRouter'],
              ['payments', 'Оплаты'],
              ['prices', 'Цены'],
              ['logs', 'Логи'],
            ] as const).map(([tab, title]) => (
              <button
                key={tab}
                className={`admin-tab ${adminTab === tab ? 'active' : ''}`}
                onClick={() => {
                  setAdminTab(tab);
                  if (tab === 'payments') loadAdminPayments();
                  if (tab === 'prices') loadAdminPrices();
                  if (tab === 'logs') loadAdminLogs(false);
                  if (tab === 'openrouter') loadV0Keys();
                  if (tab === 'support') { loadAdminSupportChats(); setAdminSupportSelected(null); setAdminSupportDetail(null); }
                  if (tab === 'feedback') loadAdminFeedback('all');
                }}
              >
                {title}
              </button>
            ))}
          </div>

          <div className="settings-content">
            {adminTab === 'stats' && adminStats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{adminStats.total_users}</div>
                  <div className="stat-label">Пользователей</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{adminStats.total_chats}</div>
                  <div className="stat-label">Чатов</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{adminStats.total_tokens_used}</div>
                  <div className="stat-label">Токенов использовано</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{adminStats.total_keys_generated}</div>
                  <div className="stat-label">Ключей создано</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{adminStats.total_activations}</div>
                  <div className="stat-label">Активаций</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{adminStats.system ? adminStats.system.cpu_percent + '%' : '—'}</div>
                  <div className="stat-label">CPU</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{adminStats.system ? adminStats.system.ram_percent + '%' : '—'}</div>
                  <div className="stat-label">{adminStats.system ? 'RAM (' + adminStats.system.ram_used_gb + '/' + adminStats.system.ram_total_gb + ' GB)' : 'RAM'}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{adminStats.system ? adminStats.system.uptime : '—'}</div>
                  <div className="stat-label">Аптайм</div>
                </div>
              </div>
            )}

            {adminTab === 'support' && (
              <div className="support-admin">
                {adminSupportSelected === null ? (
                  <>
                    {adminSupportChats.length === 0 && <p className="promo-label">Обращений пока нет</p>}
                    {adminSupportChats.map((c) => (
                      <div key={c.id} className="support-admin-item-wrap">
                        <div
                          className={`support-admin-item ${c.unread_by_admin ? 'unread' : ''}`}
                          onClick={() => adminSupportMenuFor === c.id ? setAdminSupportMenuFor(null) : openAdminSupportChat(c.id)}
                          onTouchStart={() => handleSupportLongPressStart(c.id)}
                          onTouchEnd={handleSupportLongPressEnd}
                          onContextMenu={(e) => { e.preventDefault(); setAdminSupportMenuFor(c.id); }}
                        >
                          {c.unread_by_admin && <span className="support-unread-dot" />}
                          <div className="support-admin-item-main">
                            <div className="support-admin-email">{c.email}</div>
                            <div className="support-admin-preview">{c.last_sender === 'admin' ? 'Вы: ' : ''}{c.last_message}</div>
                          </div>
                          <div className="support-admin-time">{new Date(c.last_message_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}</div>
                        </div>
                        {adminSupportMenuFor === c.id && (
                          <div className="support-admin-menu">
                            <button onClick={() => blockSupportUser(c.id)}>🚫 Заблокировать пользователя</button>
                            <button onClick={() => deleteSupportChat(c.id)}>🗑 Удалить чат</button>
                            <div className="support-admin-menu-grant">
                              <input type="number" value={adminGrantAmount} onChange={(e) => setAdminGrantAmount(e.target.value)} />
                              <button onClick={() => grantSupportTokens(c.id)}>🔑 Выдать токены</button>
                            </div>
                            <button className="support-admin-menu-close" onClick={() => setAdminSupportMenuFor(null)}>Закрыть</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="support-admin-detail">
                    <div className="support-admin-detail-top">
                      <button className="support-back-btn" onClick={() => { setAdminSupportSelected(null); setAdminSupportDetail(null); }}>← Назад</button>
                      <button className="support-menu-btn" onClick={() => setAdminSupportMenuFor(adminSupportMenuFor === adminSupportSelected ? null : adminSupportSelected)}>⋮</button>
                    </div>
                    {adminSupportMenuFor === adminSupportSelected && (
                      <div className="support-admin-menu">
                        <button onClick={() => blockSupportUser(adminSupportSelected)}>🚫 Заблокировать пользователя</button>
                        <button onClick={() => deleteSupportChat(adminSupportSelected)}>🗑 Удалить чат</button>
                        <div className="support-admin-menu-grant">
                          <input type="number" value={adminGrantAmount} onChange={(e) => setAdminGrantAmount(e.target.value)} />
                          <button onClick={() => grantSupportTokens(adminSupportSelected)}>🔑 Выдать токены</button>
                        </div>
                      </div>
                    )}
                    {adminSupportDetail && (
                      <>
                        <div className="support-user-card">
                          <div>{adminSupportDetail.user.email}</div>
                          <div className="support-user-meta">
                            🔑 {adminSupportDetail.user.tokens_used}/{adminSupportDetail.user.tokens_limit} · баланс {adminSupportDetail.user.paid_balance}
                            {adminSupportDetail.user.sub_plan ? ` · ${adminSupportDetail.user.sub_plan.toUpperCase()}` : ''}
                            {adminSupportDetail.user.account_blocked ? ' · 🚫 заблокирован' : ''}
                          </div>
                        </div>
                        {adminSupportDetail.recent_logs?.length > 0 && (
                          <details className="support-recent-logs">
                            <summary>Последние сообщения в обычном чате ({adminSupportDetail.recent_logs.length})</summary>
                            {adminSupportDetail.recent_logs.map((l: any, i: number) => (
                              <div key={i} className="support-log-line"><b>{l.role === 'user' ? 'Юзер' : 'AI'}:</b> {l.content}</div>
                            ))}
                          </details>
                        )}
                        <div className="support-messages support-admin-messages">
                          {adminSupportDetail.messages.map((m: any, i: number) => (
                            <div key={i} className={`support-msg ${m.sender === 'admin' ? 'from-admin' : 'from-user'}`}>
                              <div className="support-msg-bubble">{m.message}</div>
                              <div className="support-msg-time">{new Date(m.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                            </div>
                          ))}
                        </div>
                        <div className="support-input-row">
                          <input
                            className="auth-input"
                            type="text"
                            placeholder="Ответ..."
                            value={adminSupportReply}
                            onChange={(e) => setAdminSupportReply(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') sendAdminSupportReply(); }}
                          />
                          <button className="auth-btn support-send-btn" onClick={sendAdminSupportReply} disabled={!adminSupportReply.trim()}>Отправить</button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {adminTab === 'feedback' && (
              <div className="feedback-admin">
                <div className="feedback-counts">
                  <span className="feedback-count-like">👍 {adminFeedbackCounts.likes}</span>
                  <span className="feedback-count-dislike">👎 {adminFeedbackCounts.dislikes}</span>
                </div>
                <div className="feedback-filters">
                  {(['all', 'like', 'dislike'] as const).map((f) => (
                    <button
                      key={f}
                      className={`feedback-filter-btn ${adminFeedbackFilter === f ? 'active' : ''}`}
                      onClick={() => loadAdminFeedback(f)}
                    >
                      {f === 'all' ? 'Все' : f === 'like' ? '👍 Положительные' : '👎 Отрицательные'}
                    </button>
                  ))}
                </div>
                {adminFeedback.length === 0 && <p className="promo-label">Отзывов пока нет</p>}
                {adminFeedback.map((f) => (
                  <div key={f.id} className={`feedback-card ${f.rating}`}>
                    <div className="feedback-card-top">
                      <span className="feedback-card-rating">{f.rating === 'like' ? '👍' : '👎'}</span>
                      <span className="feedback-card-email">{f.email}</span>
                      <span className="feedback-card-time">{new Date(f.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      <button className="feedback-card-del" onClick={() => deleteAdminFeedback(f.id)}>✕</button>
                    </div>
                    {f.comment && <div className="feedback-card-comment">«{f.comment}»</div>}
                    <details className="feedback-card-ai-text">
                      <summary>Ответ нейросети, под которым оставлен отзыв</summary>
                      <div>{f.ai_text}</div>
                    </details>
                  </div>
                ))}
              </div>
            )}

            {adminTab === 'keys' && (
              <>
                <div className="key-gen-section">
                  <p className="promo-label">Генерация ключа</p>
                  <input className="auth-input" type="number" placeholder="Кол-во токенов" value={keyTokens} onChange={(e) => setKeyTokens(e.target.value)} />
                  <input className="auth-input" type="number" placeholder="Макс. активаций" value={keyActivations} onChange={(e) => setKeyActivations(e.target.value)} />
                  <input className="auth-input" type="number" placeholder="Дней до истечения (опц.)" value={keyExpireDays} onChange={(e) => setKeyExpireDays(e.target.value)} />
                  <button className="auth-btn" onClick={generateKey}>Создать ключ</button>
                  {generatedKey && (
                    <div className="generated-key" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <code style={{flex: '1 1 auto', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere'}}>{generatedKey}</code>
                      <button
                        className="auth-btn"
                        style={{padding: '6px 12px', width: 'auto', flexShrink: 0, marginTop: 0}}
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(generatedKey);
                          } catch {
                            const ta = document.createElement('textarea');
                            ta.value = generatedKey; document.body.appendChild(ta); ta.select();
                            try { document.execCommand('copy'); } catch {}
                            document.body.removeChild(ta);
                          }
                          setKeyCopied(true);
                          setTimeout(() => setKeyCopied(false), 2000);
                        }}
                      >
                        {keyCopied ? '✓' : '📋'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="keys-list">
                  <p className="promo-label">Последние ключи</p>
                  {adminStats?.recent_keys?.map((k: any, idx: number) => (
                    <div key={idx} className="key-item">
                      <div style={{display: 'flex', justifyContent: 'space-between'}}>
                        <div>
                          <code>{k.key_code}</code>
                          <div className="key-details">
                        {k.tokens_amount} 🔑 | {k.current_activations}/{k.max_activations} исп.
                        {k.expires_at && ` | до ${new Date(k.expires_at).toLocaleDateString()}`}
                          </div>
                        </div>
                        <button onClick={() => deleteKey(k.key_code)} style={{background: 'rgba(255,100,100,0.3)', border: 'none', borderRadius: '6px', padding: '4px 8px', color: '#ff6464', cursor: 'pointer'}}>🗑</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {adminTab === 'openrouter' && (
              <>
                <div style={{display: 'flex', gap: 6, marginBottom: 14}}>
                  {(['openrouter', 'glm', 'groq'] as const).map((p) => (
                    <button
                      key={p}
                      onClick={() => { setKeyProvider(p); setV0Keys([]); loadV0Keys(p); }}
                      style={{
                        flex: 1, padding: '8px 4px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: keyProvider === p ? 700 : 400,
                        background: keyProvider === p ? 'rgba(217,119,87,0.25)' : 'rgba(255,255,255,0.06)',
                        color: keyProvider === p ? '#D97757' : '#aaa',
                      }}
                    >
                      {p === 'openrouter' ? 'OpenRouter' : p === 'glm' ? 'GLM' : 'Groq'}
                    </button>
                  ))}
                </div>
                <div className="key-gen-section">
                  <p className="promo-label">Добавить API-ключ {keyProvider === 'openrouter' ? 'OpenRouter' : keyProvider === 'glm' ? 'GLM (z.ai)' : 'Groq'}</p>
                  <input
                    className="auth-input"
                    type="text"
                    placeholder={keyProvider === 'openrouter' ? 'sk-or-...' : keyProvider === 'glm' ? 'ключ z.ai' : 'gsk_...'}
                    value={newV0Key}
                    onChange={(e) => setNewV0Key(e.target.value)}
                  />
                  <input
                    className="auth-input"
                    type="text"
                    placeholder="Метка (например: аккаунт 1)"
                    value={newV0Label}
                    onChange={(e) => setNewV0Label(e.target.value)}
                  />
                  <button className="auth-btn" onClick={addV0Key}>Добавить ключ</button>
                  {keyProvider === 'openrouter' && (
                    <button
                      className="auth-btn"
                      style={{marginTop: 8, background: 'rgba(255,255,255,0.12)'}}
                      onClick={checkV0Keys}
                      disabled={v0KeysChecking}
                    >
                      {v0KeysChecking ? 'Проверяю ключи…' : 'Проверить все ключи'}
                    </button>
                  )}
                  {v0KeysMsg && <div className="promo-msg">{v0KeysMsg}</div>}
                </div>

                <div className="keys-list">
                  <p className="promo-label">Ключи {keyProvider === 'openrouter' ? 'OpenRouter' : keyProvider === 'glm' ? 'GLM' : 'Groq'} ({v0Keys.length})</p>
                  {v0Keys.length === 0 && (
                    <div className="thinking-warn">Ключей пока нет для этого сервиса.</div>
                  )}
                  {v0Keys.map((k: any) => (
                    <div key={k.id} className="key-item">
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8}}>
                        <div style={{minWidth: 0}}>
                          <code>{k.key_masked}</code>
                          {k.label && <span style={{marginLeft: 8, opacity: 0.7, fontSize: 12}}>{k.label}</span>}
                          <div className="key-details">
                            <span className={`v0-status v0-status-${k.status}`}>{keyProvider === 'openrouter' ? k.status_label : '—'}</span>
                            {' | '}{k.total_requests} запр.
                            {k.last_used && ` | посл.: ${new Date(k.last_used).toLocaleString()}`}
                          </div>
                          {k.last_error && k.status !== 'ok' && (
                            <div className="key-details" style={{color: '#ff9864'}}>{k.last_error}</div>
                          )}
                        </div>
                        <div style={{display: 'flex', gap: 6, flexShrink: 0}}>
                          <button
                            onClick={() => toggleV0Key(k.id)}
                            style={{background: k.active ? 'rgba(100,255,150,0.2)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, padding: '4px 8px', color: k.active ? '#6dff96' : '#aaa', cursor: 'pointer', fontSize: 12}}
                          >
                            {k.active ? 'Вкл' : 'Выкл'}
                          </button>
                          <button
                            onClick={() => deleteV0Key(k.id)}
                            style={{background: 'rgba(255,100,100,0.3)', border: 'none', borderRadius: 6, padding: '4px 8px', color: '#ff6464', cursor: 'pointer'}}
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {adminTab === 'payments' && (
              <div className="admin-payments">
                <div className="stat-card" style={{marginBottom: '16px'}}>
                  <div className="stat-value">{adminPayments ? adminPayments.total_paid_rub.toFixed(0) + ' ₽' : '—'}</div>
                  <div className="stat-label">Всего оплачено</div>
                </div>
                {(adminPayments?.payments || []).length === 0 && (
                  <p style={{color: '#888', fontSize: '13px'}}>Платежей пока нет</p>
                )}
                {(adminPayments?.payments || []).map((p: any) => (
                  <div
                    key={p.id}
                    className="key-item"
                    style={{
                      borderLeft: p.status === 'paid' ? '3px solid #4caf7d' : '3px solid #e0a030',
                      marginBottom: '6px'
                    }}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px'}}>
                      <span>{p.status === 'paid' ? '✅' : '⏳'} {p.email || `user#${p.user_id}`}</span>
                      <span style={{fontWeight: 700}}>{p.amount} ₽</span>
                    </div>
                    <div className="key-details" style={{marginTop: '4px'}}>
                      {p.kind === 'tokens' ? `${p.tokens.toLocaleString('ru-RU')} токенов` : p.kind === 'sub_pro' ? 'Подписка PRO' : 'Подписка MAX'}
                      {' · '}{new Date(p.created_at).toLocaleString()}
                      {p.paid_at && ` · оплачен ${new Date(p.paid_at).toLocaleString()}`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {adminTab === 'prices' && (
              <div className="admin-prices">
                <p className="promo-label">Изменение цен</p>
                {([
                  ['price_per_10k', 'Цена за 10 000 токенов, ₽'],
                  ['min_tokens', 'Мин. покупка, токенов'],
                  ['max_tokens', 'Макс. покупка, токенов'],
                  ['sub_pro_price', 'PRO: цена, ₽/мес'],
                  ['sub_pro_limit', 'PRO: лимит токенов / 4ч'],
                  ['sub_max_price', 'MAX: цена, ₽/мес'],
                  ['sub_max_limit', 'MAX: лимит токенов / 4ч'],
                ] as const).map(([key, title]) => (
                  <div key={key} style={{marginBottom: '10px'}}>
                    <div style={{fontSize: '12px', color: '#999', marginBottom: '4px'}}>{title}</div>
                    <input
                      className="auth-input"
                      style={{margin: 0}}
                      type="number"
                      value={adminPrices[key] || ''}
                      onChange={(e) => setAdminPrices({ ...adminPrices, [key]: e.target.value })}
                    />
                  </div>
                ))}
                <button className="auth-btn" onClick={saveAdminPrices}>Сохранить цены</button>
                {pricesMsg && <div className="promo-msg">{pricesMsg}</div>}
              </div>
            )}

            {adminTab === 'logs' && (
            <div className="admin-logs-section">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                <p className="promo-label" style={{margin: 0}}>Логи запросов</p>
                <div style={{display: 'flex', gap: '8px'}}>
                  <button
                    onClick={() => loadAdminLogs(false)}
                    style={{
                      background: !showErrorLogsOnly ? '#D97757' : 'rgba(255,255,255,0.08)',
                      border: 'none', borderRadius: '6px', padding: '6px 12px',
                      color: '#fff', cursor: 'pointer', fontSize: '13px'
                    }}
                  >
                    Все
                  </button>
                  <button
                    onClick={() => loadAdminLogs(true)}
                    style={{
                      background: showErrorLogsOnly ? '#ff6464' : 'rgba(255,100,100,0.2)',
                      border: 'none', borderRadius: '6px', padding: '6px 12px',
                      color: '#fff', cursor: 'pointer', fontSize: '13px'
                    }}
                  >
                    ⚠️ Ошибки
                  </button>
                </div>
              </div>
              <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                {adminLogs.length === 0 && (
                  <p style={{color: '#888', fontSize: '13px'}}>Логов пока нет</p>
                )}
                {adminLogs.map((log: any) => (
                  <div
                    key={log.id}
                    className="key-item"
                    style={{
                      borderLeft: log.status === 'error' ? '3px solid #ff6464' : '3px solid #4caf7d',
                      marginBottom: '6px'
                    }}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px'}}>
                      <span>{log.status === 'error' ? '⚠️' : '✅'} {log.email || `user#${log.user_id}`}</span>
                      <span style={{color: '#888'}}>{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                    <div className="key-details" style={{marginTop: '4px'}}>
                      chat #{log.chat_id} · {log.model} · {log.tokens_used} 🔑
                      {log.error_message && (
                        <div style={{color: '#ff8888', marginTop: '2px', wordBreak: 'break-word'}}>
                          {log.error_message}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
